import _ from 'lodash'
import * as THREE from 'three'

import EditorUtils from '@glass-designer/utils/editorUtils'
import EditorDatumUtils from '@glass-designer/utils/editorDatumUtils'
import MathsUtils from '@/utils/mathsUtils'
import Utils from '@/utils/utils'

import { EditorUserError } from '@glass-designer/utils/editorErrors'
import { UserError } from '@/utils/errors'

import LibraryItemDialog from '@glass-designer/views/libraryItemDialog'
import GlassItemDialog from '@glass-designer/views/glassItemDialog'
import EditorAssemblyItemDialog from '@glass-designer/views/editorAssemblyItemDialog'

import BaseTool from '@glass-designer/tools/baseTool'
import GlassShape from '@glass-designer/model/glassShape'
import GlassSegment from '@/model/glassSegment'
import Config from '@glass-designer/config'

import EditorTypes from '@glass-designer/types'
import GlassDatumLine from '@glass-designer/model/glassDatumLine'
import GlassBaseHole from '@glass-designer/model/glassBaseHole'
import GlassPanelItem from '@glass-designer/model/glassPanelItem'
import GlassSplitLine from '@glass-designer/model/glassSplitLine'
import GlassShapeGroup from '@glass-designer/model/glassShapeGroup'
import CustomHole from '@glass-designer/model/holes/customHole'
import WarningIconObject from '@glass-designer/objects/warningIconObject'
import AssemblyGroup from '@glass-designer/model/assemblyGroup'

import { InventoryStockTypes } from '@/store/types'
import GlassDesignerTypes from '@glass-designer/types'


// Select tool
// Primary/Default tool used for selection of areas/items in the editor
export default class SelectTool extends BaseTool {
  constructor(editor) {
    super(editor)

    this.type = EditorTypes.ToolTypes.selectTool
    this.selectionBox = {
      started: false,
      active: false,
      start: new THREE.Vector2(0, 0),
      end: new THREE.Vector2(10, 10)
    }

    this.selectionBoxVertices = []
    this.selectionBoxOutlineVertices = []

    this.selectedSegmentList = []
    this.selectedStartSegmentList = []
    this.selectedList = []

    // Selection options
    this.optionRemoveAvailable = false

    // Setup the drawing box
    this.lineGeometry = new THREE.BufferGeometry()
    this.boxGeometry = new THREE.BufferGeometry()

    // 3 verts per face. 2 faces for the box
    // Dummy data added until used
    for(var i=0; i<6; i++)
      this.selectionBoxVertices.push(i*10, i*10, 99)
    
    // 2 verts per segment. 4 segments for the box
    // Dummy data added until used
    for(var n=0; n<8; n++) 
      this.selectionBoxOutlineVertices.push(n*10, n*10, 99)

    this.lineGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute( this.selectionBoxOutlineVertices, 3 ) )
    this.lineGeometry.computeVertexNormals()

    this.selectionLine = new THREE.LineSegments(this.lineGeometry, new THREE.LineBasicMaterial({
      color: this.editor.getTheme().selectionBoxOutlineColour,
      transparent: true,
      opacity: this.editor.getTheme().selectionBoxOutlineOpacity
    }))
    this.selectionLine.name = "line"
    this.sceneObject.add(this.selectionLine)

    this.boxGeometry.setAttribute( 'position', new THREE.Float32BufferAttribute( this.selectionBoxVertices, 3 ) )
    this.boxGeometry.computeVertexNormals()

    this.selectionBoxObj = new THREE.Mesh(this.boxGeometry, new THREE.MeshBasicMaterial({
      color: this.editor.getTheme().selectionBoxColour,
      transparent: true,
      opacity: this.editor.getTheme().selectionBoxOpacity,
      side: THREE.DoubleSide,
      depthWrite: false
    }))
    this.sceneObject.add(this.selectionBoxObj)
  }


  // Tool Active
  // Called when the tool is active
  onToolActive(options = {}) {
    super.onToolActive(options)

    this.displayEditSelectionControls()
  }

  // Tool Inactive
  // Called when the tool is no longer active
  onToolInactive() {
    super.onToolInactive()
  }


  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    this.selectionLine.visible = this.selectionBox.active
    this.selectionBoxObj.visible = this.selectionBox.active

    if(!this.selectionBox.active)
      return

    // Rebuild the drawing box outline geometry
    // For now we assume it's 2D
    // Sets the vertices manually
    this.selectionBoxOutlineVertices[0] = this.selectionBoxOutlineVertices[15] = this.selectionBoxOutlineVertices[18] = this.selectionBoxOutlineVertices[21] = this.selectionBox.start.x
    this.selectionBoxOutlineVertices[1] = this.selectionBoxOutlineVertices[4] = this.selectionBoxOutlineVertices[7] = this.selectionBoxOutlineVertices[22] = this.selectionBox.start.y

    this.selectionBoxOutlineVertices[3] = this.selectionBoxOutlineVertices[6] = this.selectionBoxOutlineVertices[9] = this.selectionBoxOutlineVertices[12] = this.selectionBox.end.x
    this.selectionBoxOutlineVertices[10] = this.selectionBoxOutlineVertices[13] = this.selectionBoxOutlineVertices[16] = this.selectionBoxOutlineVertices[19] = this.selectionBox.end.y

    this.selectionLine.geometry.setAttribute('position', new THREE.Float32BufferAttribute( this.selectionBoxOutlineVertices, 3 ))
    this.selectionLine.geometry.computeVertexNormals()
    this.selectionLine.geometry.computeBoundingSphere()
    this.selectionLine.geometry.computeBoundingBox()

    // Rebuild the drawing box geometry
    // For now we assume it's 2D
    // Sets the vertices manually
    this.selectionBoxVertices[0] = this.selectionBoxVertices[9] = this.selectionBoxVertices[15] = this.selectionBox.start.x
    this.selectionBoxVertices[1] = this.selectionBoxVertices[4] = this.selectionBoxVertices[10] = this.selectionBox.start.y

    this.selectionBoxVertices[3] = this.selectionBoxVertices[6] = this.selectionBoxVertices[12] = this.selectionBox.end.x
    this.selectionBoxVertices[7] = this.selectionBoxVertices[13] = this.selectionBoxVertices[16] = this.selectionBox.end.y

    this.selectionBoxObj.geometry.setAttribute('position', new THREE.Float32BufferAttribute( this.selectionBoxVertices, 3 ))
    this.selectionBoxObj.geometry.computeVertexNormals()
    this.selectionBoxObj.geometry.computeBoundingSphere()
    this.selectionBoxObj.geometry.computeBoundingBox()
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    super.onMouseDownEvent(ev, mouseData)

    if(!this.isSelectAvailable())
      return false


    if(ev.button == Config.Mouse.buttonSelect) {
      this.selectionBox.started = true
      this.selectionBox.active = false
      this.selectionBox.start = this.selectionBox.end = mouseData.position.clone()
    }

    this.editor.flagRenderRequired()

    return false
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    super.onMouseMoveEvent(ev, mouseData)

    var viewScale = 1.0 / this.editor.camera.zoom
    let minSizeThreshold = 5 * viewScale

    if(!this.isSelectAvailable())
      return false

    if(this.selectionBox.started) {
      this.selectionBox.end = mouseData.position.clone()

      // If selection box big enough, flag it as active so nothing else gets the mouse up events
      let maxSize = Math.max(Math.abs(this.selectionBox.end.x - this.selectionBox.start.x), Math.abs(this.selectionBox.end.y - this.selectionBox.start.y))
      if(maxSize >= minSizeThreshold)
        this.selectionBox.active = true

      this.editor.flagRenderRequired()
    }

    return this.selectionBox.active
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData) {
    var eventHandled = false
    super.onMouseUpEvent(ev, mouseData)

    if(!this.isSelectAvailable())
      return false

    this.editor.endShapeEditingMode()

    if(this.selectionBox.started) {
      if(!ev.shiftKey && !ev.altKey) {
        this.clearSelection()
      }
    }

    // Run selection on vertices / segments within the selection box
    if(this.selectionBox.active) {
      eventHandled = true
      this._selectObjects(ev.shiftKey, ev.altKey)
    } else {
      // Check what object was clicked on & run that process
      if(ev.button == Config.Mouse.buttonSelect) {
        let closestObjectInfo = this._findClosestObjectToRay(mouseData.raycaster.ray, mouseData.radius)

        if(closestObjectInfo) {
          eventHandled = this._objectSelected(closestObjectInfo, mouseData)
        }
      }
    }

    this.selectionBox.active = false
    this.selectionBox.started = false
    this.editor.flagRenderRequired()

    return eventHandled
  }

  onMouseWheelEvent(ev, mouseData) {
    super.onMouseWheelEvent(ev, mouseData)
  }

  onTouchStartEvent(ev, touchData) {
    super.onTouchStartEvent(ev, touchData)
  }
  onTouchEndEvent(ev, touchData) {
    super.onTouchEndEvent(ev, touchData)
  }
  onTouchCancelEvent(ev, touchData) {
    super.onTouchCancelEvent(ev, touchData)
  }
  onTouchMoveEvent(ev, touchData) {
    super.onTouchMoveEvent(ev, touchData)
  }


  /**
   * Check if selection is available
   * If the view level type is not regular, then don't make selection available
   */
  isSelectAvailable() {
    return this.editor.getViewLevel() == GlassDesignerTypes.EditorViewLevelTypes.normal
  }


  /**
   * Run a selection on objects inside / intersecting with the selection box
   */
  _selectObjects(isAddMode = false, isRemoveMode = false) {
    let minX = Math.min(this.selectionBox.start.x, this.selectionBox.end.x)
    let minY = Math.min(this.selectionBox.start.y, this.selectionBox.end.y)
    let maxX = Math.max(this.selectionBox.start.x, this.selectionBox.end.x)
    let maxY = Math.max(this.selectionBox.start.y, this.selectionBox.end.y)

    let pointInsideSelection = (point) => {
      return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY
    }
    let lineInsideSelection = (startPosition, endPosition) => {
      return pointInsideSelection(startPosition) && pointInsideSelection(endPosition)
    }
    let boxCenterInsideSelection = (box) => {
      let cx = (box.min.x + box.max.x) / 2.0
      let cy = (box.min.y + box.max.y) / 2.0
      return cx >= minX && cx <= maxX && cy >= minY && cy <= maxY
    }

    var shapeList = this.editor.getShapeList()
    
    if(!isAddMode && !isRemoveMode)
      this.editor.clearSelection()
    
    // Go through all shapes & all segments in each shape
    shapeList.forEach(glassShape => {
      var selectedVerts = []
      var selectedSegments = []

      // Split lines
      glassShape.getSplitLineList().forEach(splitLine => {
        let lineInside = lineInsideSelection(splitLine.startPosition, splitLine.endPosition)
        if(lineInside && !isRemoveMode) {
          this.editor.selectionList.push(splitLine)

          splitLine.setSelected({}, true)
        }
      })

      // Datum lines
      glassShape.getDatumLineList().forEach(datumLine => {
        let lineInside = lineInsideSelection(datumLine.startPosition, datumLine.endPosition)
        if(lineInside && !isRemoveMode) {
          this.editor.selectionList.push(datumLine)

          datumLine.setSelected({}, true)
        }
      })

      // Ignore shapes that are stock linked from segment/vertex/hole selection
      if(glassShape.isStockLinked())
        return

      glassShape.segmentList.forEach((segment, segmentIndex) => {
        let startInside = pointInsideSelection(segment.startPosition)
        let lineInside = lineInsideSelection(segment.startPosition, segment.endPosition)

        // Check what segments are completely inside the box
        if(lineInside) {
          selectedSegments.push(segmentIndex)
          
          // Select both verts
          selectedVerts.push(segmentIndex)
          selectedVerts.push((segmentIndex + 1) % glassShape.segmentList.length)
        }
        if(startInside)
          selectedVerts.push(segmentIndex)
      })

      selectedVerts = _.uniq(selectedVerts)
      selectedSegments = _.uniq(selectedSegments)

      if(selectedVerts.length > 0 || selectedSegments.length > 0) {
        if(!isRemoveMode)
          this.editor.selectionList.push(glassShape)

        selectedVerts.forEach(segmentIndex => {
          glassShape.setSelected({ isSegmentStart: true, segmentIndex: segmentIndex }, !isRemoveMode)
        })
        selectedSegments.forEach(segmentIndex => {
          glassShape.setSelected({ isSegment: true, segmentIndex: segmentIndex }, !isRemoveMode)
        })
      }

      // Holes
      glassShape.getCenterCutoutList().forEach(hole => {
        let holeBounds = hole.calculateSegmentBounds()

        // Hole center must be inside the box
        if(boxCenterInsideSelection(holeBounds)) {
          if(!isRemoveMode) {
            this.editor.selectionList.push(hole)

            hole.setSelected({}, true)
          }
        }
      })

      // Panel items
      glassShape.getPanelItemList().forEach(panelItem => {
        let itemBounds = panelItem.calculateBounds()

        // Panel center must be inside the box
        if(boxCenterInsideSelection(itemBounds)) {
          if(!isRemoveMode) {
            this.editor.selectionList.push(panelItem)

            panelItem.setSelected({}, true)
          }
        }
      })
    })

    if(this.editor.getSelected().length > 0) {
      this.displayToolSettingsPopover()
      this.displayEditSelectionControls()
      this.selectionUpdated()
    }
  }


  /**
   * Clear the selection & hide any popover
   */
  clearSelection() {
    this.editor.clearSelection()
    this.editor.component.hidePopover()

    this.editor.endShapeEditingMode()

    this.hideEditSelectionControls()

    this.editor.flagRenderRequired()
  }


  /**
   * Select a single object (via click/touch) with info provided by findClosestObjectToRay()
   * Returns true if we handle the selection of the object
   */
  _objectSelected(objectInfo, mouseTouchData) {
    let selectedObject = this.editor.getObjectById(objectInfo.objectId)
    if(!selectedObject)
      return false

    let isShape = (selectedObject instanceof GlassShape)

    if(isShape && objectInfo.tag == EditorTypes.ShapeEditingTypes.shape) {
      this.editor.clearSelection()
      
      // Select the entire shape
      // Don't select the individual segments
      selectedObject.segmentList.forEach((segment, segmentIndex) => {
        selectedObject.setSelected({ isSegmentStart: true, segmentIndex: segmentIndex }, true)
        selectedObject.setSelected({ isSegment: true, segmentIndex: segmentIndex }, true)
      })

      // Clear local selected segments
      this.selectedSegmentList = []
      this.selectedStartSegmentList = []

      this.editor.selectionList.push(selectedObject)

      // Display shape popover
      this.displayShapePopover(selectedObject)
      this.displayEditSelectionControls()
      this.selectionUpdated()

      return true
    }

    if(isShape && objectInfo.tag == EditorTypes.ShapeEditingTypes.segment) {
      this.editor.clearSelection()
      this.editor.setSelected(selectedObject, { isSegment: true, segmentIndex: objectInfo.tagIndex }, true)

      // Start start/end vertices as selected too
      this.editor.setSelected(selectedObject, { isSegmentStart: true, segmentIndex: objectInfo.tagIndex }, true)
      this.editor.setSelected(selectedObject, { isSegmentStart: true, segmentIndex: (objectInfo.tagIndex + 1) % selectedObject.segmentList.length }, true)

      this.displayToolSettingsPopover()
      this.displayEditSelectionControls()
      this.selectionUpdated()

      return true
    }

    if(isShape && objectInfo.tag == EditorTypes.ShapeEditingTypes.segmentStart) {
      this.editor.clearSelection()
      this.editor.setSelected(selectedObject, { isSegmentStart: true, segmentIndex: objectInfo.tagIndex }, true)

      this.displayToolSettingsPopover()
      this.displayEditSelectionControls()
      this.selectionUpdated()

      return true
    }

    // Datum Line Selected
    if(selectedObject instanceof GlassDatumLine) {

      this.editor.clearSelection()
      this.editor.setSelected(selectedObject, { }, true)
      
      // Popover
      this.displayToolSettingsPopover()
      this.selectionUpdated()

      return true
    }

    // Center Cutout
    if(selectedObject instanceof GlassBaseHole) {
      this.editor.clearSelection()
      this.editor.setSelected(selectedObject, { }, true)

      // Popover
      this.displayToolSettingsPopover()

      this.displayEditSelectionControls()
      this.selectionUpdated()

      return true
    }

    // Panel Item
    if(selectedObject instanceof GlassPanelItem) {
      this.editor.clearSelection()
      this.editor.setSelected(selectedObject, { }, true)

      // Popover
      this.displayToolSettingsPopover()
      this.displayEditSelectionControls()
      this.selectionUpdated()

      return true
    }

    // Split Line
    if(selectedObject instanceof GlassSplitLine) {
      this.editor.clearSelection()
      this.editor.setSelected(selectedObject, { }, true)

      // Popover
      this.displayToolSettingsPopover()
      this.selectionUpdated()

      return true
    }

    // Shape Group
    if(selectedObject instanceof GlassShapeGroup) {
      this.editor.clearSelection()

      // Select the group instead
      this.editor.setSelected(selectedObject, { }, true)

      // Popover
      this.displayToolSettingsPopover()
      this.displayEditSelectionControls()
      this.selectionUpdated()

      return true
    }

    // Assembly Group
    if(selectedObject instanceof AssemblyGroup) {
      this.editor.clearSelection()

      // Select the group instead
      this.editor.setSelected(selectedObject, { }, true)

      // Popover
      this.displayAssemblyPopover(selectedObject)
      this.displayEditSelectionControls()
      this.selectionUpdated()

      return true
    }

    // Warnings
    if(selectedObject instanceof WarningIconObject) {
      this.editor.clearSelection()
      this.editor.setSelected(selectedObject, { }, true)

      // Display popover showing warning information
      this.editor.component.displayMessagePopover({
        title: selectedObject.warningTitle,
        message: selectedObject.warningHTMLDescription,
        fixAvailable: selectedObject.warningFixAvailable,
        context: this,
        actionCallback: (ctx, action) => {
          if(action == 'fix') {
            ctx.editor.getGuidelineHelper().fixGuidelineWarning(selectedObject.guidelineWarningId)
            ctx.editor.component.hidePopover()
          }
        }
      }, {
        horizontalPosition: this._calculatePopoverHorizontalPosition()
      })

      this.selectionUpdated()

      return true
    }


    return false
  }

  // Get the list of shapes with segment / child object selections
  _getSelectedShapeList() {
    var shapeList = this.editor.getSelected().filter(object => {
      if(object instanceof GlassShape)
        return true
      return false
    })

    // Get shapes with child objects selected
    this.editor.getSelected().forEach(object => {
      if(object.parentObject && object.parentObject instanceof GlassShape)
        shapeList.push(object.parentObject)
    })

    return _.uniqWith(shapeList, (shapeA, shapeB) => shapeA.id == shapeB.id)
  }


  // Get the list of selected assembly objects
  _getSelectedAssemblyList() {
    var assemblyList = this.editor.getSelected().filter(object => {
      if(object instanceof AssemblyGroup)
        return true
      return false
    })

    return assemblyList
  }


  /**
   * Calculate the selected index of segments used for cutouts
   * Ensures the selected list is contiguous
   * Returns the list of selected segment indexes available for a cutout
   */
   _calculateCutoutSelectedSegmentList(shape) {
    // Find first selected segment
    // Expand forwards & backwards around shape as it's a looped polygon
    // Go until unselected segment found in both directions or duplicate indices found
    var firstSelectedIndex = -1
    shape.segmentList.forEach((segment, index) => {
      if(segment.isSegmentSelected && firstSelectedIndex == -1)
        firstSelectedIndex = index
    })

    // Nothing selected, cannot do anything
    if(firstSelectedIndex == -1)
      return []

    var selectedIndexList = []
    var forwards = true
    var backwards = true
    for(var i=0; i<shape.segmentList.length; i++) {
      let fwdIndex = (firstSelectedIndex + i) % shape.segmentList.length
      var backIndex = (firstSelectedIndex - i)
      if(backIndex < 0)
        backIndex += shape.segmentList.length
      
      if(forwards) {
        if(shape.segmentList[fwdIndex].isSegmentSelected) {
          if(selectedIndexList.indexOf(fwdIndex) == -1)
            selectedIndexList.push(fwdIndex)
        } else {
          forwards = false
        }
      }
      if(backwards) {
        if(shape.segmentList[backIndex].isSegmentSelected) {
          if(selectedIndexList.indexOf(backIndex) == -1)
            selectedIndexList.splice(0, 0, backIndex)
        } else {
          backwards = false
        }
      }
      if(!forwards && !backwards)
        break
    }

    return selectedIndexList
  }

  
  /**
   * Create a new cutout library item from the selected vertices in the shape
   * Only valid for edge/corner cutouts
   */
  _createLibraryShapeCutoutItem() {
    var selectedShapeList = this._getSelectedShapeList()
    var selectedCenterCutoutList = this.editor.getSelected().filter(object => object instanceof GlassBaseHole)
    var selectedShapeGroupList = this.editor.getSelected().filter(object => object instanceof GlassShapeGroup)

    // Cannot group vertices across multiple shapes (or no shapes)
    if(selectedShapeList.length == 0)
      throw new EditorUserError('Nothing selected')
    if(selectedShapeList.length > 1)
      throw new EditorUserError('Cannot create an item from multiple panels')

    var shape = selectedShapeList[0]
    var selectedIndexList = this._calculateCutoutSelectedSegmentList(shape)

    // If no segments selected, but a group is selected, use segments linked to the group
    if(selectedIndexList.length == 0) {
      if(selectedShapeGroupList.length > 0) {
        selectedIndexList = selectedShapeGroupList[0].segmentIdList.map(segId => {
          return selectedShapeGroupList[0].parentObject.findSegmentIndexById(segId)
        }).filter(segIndex => segIndex != -1)
      }
    }

    // Make sure all selected center cutouts are associated with the shape
    selectedCenterCutoutList.forEach(centerCutout => {
      if(!centerCutout.parentObject)
        throw new EditorUserError('Center cutout selected not associated with the selected panel')
      if(centerCutout.parentObject.id != shape.id)
        throw new EditorUserError('Center cutout selected not associated with the selected panel')

      if(centerCutout instanceof CustomHole)
        throw new EditorUserError('Cutom center cutouts not supported within cutouts')
    })

    let totalSelected = shape.segmentList.filter(segment => segment.isSegmentSelected).length

    var segmentList = selectedIndexList.map(index => shape.segmentList[index])
    if(segmentList.length < totalSelected)
      throw new EditorUserError('All selected edges must be connected')
    if(segmentList.length <= 0)
      throw new EditorUserError('At least one edge must be selected')

    var segmentListCopy = segmentList.map(segment => segment.clone())

    // Ensure the segments are clockwise
    var pointList = MathsUtils.convertSegmentListToPointList(segmentListCopy)
    pointList = MathsUtils.removeConsecutive2DPointDuplicates(pointList, 0.001)

    if(MathsUtils.get2DPolygonOrientation(pointList) > 0) {
      segmentListCopy = MathsUtils.reverseGlassSegmentList(segmentListCopy)
    }

    var startPos = segmentListCopy[0].startPosition.clone()
    var endPos = segmentListCopy[segmentListCopy.length-1].endPosition.clone()
    var moveOffset = startPos.clone().negate()

    // Cleanup the segment list for the library item
    // Adjust the segment positions so they are offset from the first segment
    EditorUtils.cleanupLibraryItemGlassSegmentList(segmentListCopy, moveOffset)
    var outputSegments = segmentListCopy.map(segment => segment.serialize())

    var thumbnailSegments = [
      segmentList
    ]

    // Prepare the center cutout list
    var centerCutoutList = selectedCenterCutoutList.map(centerCutout => {
      // Reverse segment lists from holes for thumbnails
      var centerSegmentListCopy = centerCutout.segmentList.map(segment => segment.clone())
      centerSegmentListCopy = MathsUtils.reverseGlassSegmentList(centerSegmentListCopy)
      thumbnailSegments.push(centerSegmentListCopy)

      return {
        cutoutData: centerCutout.serialize(),
        offset: {
          x: centerCutout.worldPoint.x - startPos.x,
          y: centerCutout.worldPoint.y - startPos.y
        }
      }
    })

    // Generate a thumbnail based on the vertices
    let thumbnailData = EditorUtils.generateCutoutThumbnailDataURI(thumbnailSegments, Config.Settings.libraryThumbnailSize, Config.Settings.libraryThumbnailSize)

    // Attempt to figure out default type based on vertices
    // If start/end on the same X or Y axis, then it's most likely an edge
    var libraryItemType = EditorTypes.LibraryItemTypes.cornerCutOut

    let edgeAxisTolerance = 1.0
    if(Math.abs(startPos.x - endPos.x) < edgeAxisTolerance || Math.abs(startPos.y - endPos.y) < edgeAxisTolerance)
      libraryItemType = EditorTypes.LibraryItemTypes.edgeCutOut

    let libraryItem = {
      type: libraryItemType,
      thumbnailImageData: thumbnailData,
      itemData: {
        cutoutList: [{
          cutoutData: {
            shapeType: EditorTypes.LibraryItemShapeTypes.custom,
            segmentList: outputSegments
          }
        }],
        centerCutoutList: centerCutoutList
      }
    }

    // Item entry
    this.editor.component.displayModal(LibraryItemDialog, {
      isCreateMode: true,
      typeFilter: [EditorTypes.LibraryItemTypes.edgeCutOut, EditorTypes.LibraryItemTypes.cornerCutOut] }, libraryItem, { mountBody: true })
    .then(libraryItemInfo => {
      if(libraryItemInfo) {
        this.editor.component.successToast('Item Created')
        this.editor.component.updateLibrary()
      }
    })
  }


  /**
   * Create a new cutout library item from the selected center cutouts
   * Only valid for center cutouts
   */
  _createLibraryCenterCutoutItem() {
    var selectedCenterCutoutList = this.editor.getSelected().filter(object => object instanceof GlassBaseHole)

    var totalBounds = new THREE.Box2()

    var cutoutSegList = selectedCenterCutoutList.map(cutout => {
      // Reverse segment lists from holes for thumbnails
      var segmentListCopy = cutout.segmentList.map(segment => segment.clone())
      segmentListCopy = MathsUtils.reverseGlassSegmentList(segmentListCopy)

      let cutoutBounds = cutout.calculateSegmentBounds()
      totalBounds = totalBounds.union(cutoutBounds)

      return segmentListCopy
    })

    let thumbnailSize = Config.Settings.libraryThumbnailSize
    let thumbnailData = EditorUtils.generateCutoutThumbnailDataURI(cutoutSegList, thumbnailSize, thumbnailSize)

    var libraryItemType = EditorTypes.LibraryItemTypes.centerCutOut

    var selectionOrigin = totalBounds.getCenter(new THREE.Vector2())

    // Generate cutout data list
    var itemDataCutoutList = selectedCenterCutoutList.map(cutout => {

      // Get origin for each cutout
      let cutoutBounds = cutout.calculateSegmentBounds()
      let cutoutCenter = cutoutBounds.getCenter(new THREE.Vector2())

      return {
        cutoutData: cutout.serialize(),
        offset: {
          x: selectionOrigin.x - cutoutCenter.x,
          y: selectionOrigin.y - cutoutCenter.y
        }
      }
    })

    let libraryItem = {
      type: libraryItemType,
      thumbnailImageData: thumbnailData,
      itemData: {
        cutoutList: itemDataCutoutList
      }
    }

    // Item entry
    this.editor.component.displayModal(LibraryItemDialog, {
      isCreateMode: true,
      typeFilter: [EditorTypes.LibraryItemTypes.centerCutOut]
    }, libraryItem, { mountBody: true })
    .then(libraryItemInfo => {
      if(libraryItemInfo) {
        this.editor.component.successToast('Item Created')
        this.editor.component.updateLibrary()
      }
    })


  }


  /**
   * Create a new shape / kit library item from the selected shapes
   */
  _createLibraryShapeKitItem() {
    var selectedShapeList = this._getSelectedShapeList()

    // Need at least one shape
    if(selectedShapeList.length == 0)
      throw new EditorUserError('Nothing selected')

    var outputKitList = []

    // Total bounds
    var totalShapeBounds = new THREE.Box2()
    selectedShapeList.forEach(shape => {
      let shapeBounds = MathsUtils.calculateGlassSegmentListBounds(shape.segmentList)
      totalShapeBounds.union(shapeBounds)
    })    

    // Segments across the shapes must all be selected
    selectedShapeList.forEach(shape => {
      var selectedIndexList = this._calculateCutoutSelectedSegmentList(shape)

      let totalSelected = shape.segmentList.filter(segment => segment.isSegmentSelected).length
      var segmentList = selectedIndexList.map(index => shape.segmentList[index])
      if(segmentList.length != totalSelected)
        throw new EditorUserError('All segments must be selected')

      var segmentListCopy = segmentList.map(segment => {
        // Copy of the segment
        // Very IMPORTANT to keep the ID as other objects (eg, datum links, groups) reference it
        let newSeg = segment.clone()
        newSeg.id = segment.id    
        return newSeg
      })

      // Ensure the segments are clockwise
      var pointList = MathsUtils.convertSegmentListToPointList(segmentListCopy)
  
      if(MathsUtils.get2DPolygonOrientation(pointList) > 0) {
        segmentListCopy = MathsUtils.reverseGlassSegmentList(segmentListCopy)
      }
  
      var origin = totalShapeBounds.min.clone().negate()
      EditorUtils.cleanupLibraryItemGlassSegmentList(segmentListCopy, origin)
  
      // Cleanup the segment list for the library item
      // Adjust the segment positions so they are offset from the shape center
      var outputSegments = segmentListCopy.map(segment => segment.serialize())

      // Copy over the center cutouts of the shape
      var centerCutouts = shape.getCenterCutoutList().map(hole => {
        return {
          holeObj: hole,
          offset: hole.worldPoint.clone().sub(totalShapeBounds.min)
        }
      })

      // Copy over the shape groups
      var shapeGroups = shape.getShapeGroupList()

      outputKitList.push({
        shapeType: EditorTypes.LibraryItemShapeTypes.custom,
        segmentList: segmentList,
        segmentDataList: outputSegments,
        centerCutoutList: centerCutouts,
        shapeGroupList: shapeGroups
      })      
    })

    // Generate a thumbnail based on the vertices
    var thumbSegmentList = []
    outputKitList.forEach(shape => {
      thumbSegmentList.push(shape.segmentList)

      shape.centerCutoutList.forEach(centerCutoutInfo => {
        thumbSegmentList.push(centerCutoutInfo.holeObj.segmentList)
      })
    })

    let thumbnailData = EditorUtils.generateCutoutThumbnailDataURI(thumbSegmentList, Config.Settings.libraryThumbnailSize, Config.Settings.libraryThumbnailSize)

    var libraryItemType = EditorTypes.LibraryItemTypes.shape
    if(outputKitList.length > 1)
      libraryItemType = EditorTypes.LibraryItemTypes.kit

    let libraryItem = {
      type: libraryItemType,
      thumbnailImageData: thumbnailData,
      itemData: {
        shapeList: outputKitList.map(shape => {
          return {
            shapeData: {
              shapeType: shape.shapeType,
              segmentList: shape.segmentDataList,
              centerCutoutList: shape.centerCutoutList.map(centerCutoutInfo => {
                return {
                  cutoutData: centerCutoutInfo.holeObj.serialize(),
                  offset: MathsUtils.serializeVector2(centerCutoutInfo.offset)
                }
              }),
              shapeGroupList: shape.shapeGroupList.map(shapeGroup => {
                return {
                  groupData: shapeGroup.serialize()
                }
              })
            }
          }
        })
      }
    }

    // Item entry
    this.editor.component.displayModal(LibraryItemDialog, {
      isCreateMode: true,
      typeFilter: [libraryItemType]
    }, libraryItem, { mountBody: true })
    .then(libraryItemInfo => {
      if(libraryItemInfo) {
        this.editor.component.successToast('Item Created')
        this.editor.component.updateLibrary()
      }
    })
  }


  /**
   * Create a new assembly library item from the selected shapes
   */
  _createLibraryAssemblyItem() {
    var selectedAssemblyList = this._getSelectedAssemblyList()

    // Can only handle 1 assembly
    if(selectedAssemblyList.length == 0)
      throw new EditorUserError('Nothing selected')
    if(selectedAssemblyList.length > 1)
      throw new EditorUserError('Only 1 assembly can be selected')

    var assemblyGroup = selectedAssemblyList[0]
    var childShapeList = assemblyGroup.getChildGlassShapeList()
    var outputChildList = []

    // Total bounds
    var totalShapeBounds = new THREE.Box2()
    childShapeList.forEach(shape => {
      let shapeBounds = MathsUtils.calculateGlassSegmentListBounds(shape.segmentList)
      totalShapeBounds.union(shapeBounds)
    })

    childShapeList.forEach(shape => {
      let segmentList = shape.segmentList

      var segmentListCopy = segmentList.map(segment => {
        // Copy of the segment
        // Very IMPORTANT to keep the ID as other objects (eg, datum links, groups) reference it
        let newSeg = segment.clone()
        newSeg.id = segment.id    
        return newSeg
      })

      // Ensure the segments are clockwise
      var pointList = MathsUtils.convertSegmentListToPointList(segmentListCopy)
  
      if(MathsUtils.get2DPolygonOrientation(pointList) > 0) {
        segmentListCopy = MathsUtils.reverseGlassSegmentList(segmentListCopy)
      }
  
      var origin = totalShapeBounds.min.clone().negate()
      EditorUtils.cleanupLibraryItemGlassSegmentList(segmentListCopy, origin)
  
      // Cleanup the segment list for the library item
      // Adjust the segment positions so they are offset from the shape center
      var outputSegments = segmentListCopy.map(segment => segment.serialize())

      // Copy over the center cutouts of the shape
      var centerCutouts = shape.getCenterCutoutList().map(hole => {
        return {
          holeObj: hole,
          offset: hole.worldPoint.clone().sub(totalShapeBounds.min)
        }
      })

      // Copy over the shape groups
      let shapeGroups = shape.getShapeGroupList()
      let shapeData = shape.serialize()

      // Cleanup shape data for library item
      // Note: Retain assembly UUID as need that to remap to the assembly order item data. It'll get regenerated on load
      shapeData.id = null

      outputChildList.push({
        shapeData: shapeData,
        shapeType: EditorTypes.LibraryItemShapeTypes.custom,
        segmentList: segmentList,
        segmentDataList: outputSegments,
        centerCutoutList: centerCutouts,
        shapeGroupList: shapeGroups
      })      
    })

    // Generate a thumbnail based on the vertices
    var thumbSegmentList = []
    outputChildList.forEach(shape => {
      thumbSegmentList.push(shape.segmentList)

      shape.centerCutoutList.forEach(centerCutoutInfo => {
        thumbSegmentList.push(centerCutoutInfo.holeObj.segmentList)
      })
    })

    let thumbnailData = EditorUtils.generateCutoutThumbnailDataURI(thumbSegmentList, Config.Settings.libraryThumbnailSize, Config.Settings.libraryThumbnailSize)

    let libraryItem = {
      type: EditorTypes.LibraryItemTypes.assembly,
      thumbnailImageData: thumbnailData,
      itemData: {
        assemblyData: assemblyGroup.serialize(),
        shapeList: outputChildList.map(shape => {
          return {
            shapeData: {
              shapeData: shape.shapeData,
              shapeType: shape.shapeType,
              segmentList: shape.segmentDataList,
              centerCutoutList: shape.centerCutoutList.map(centerCutoutInfo => {
                return {
                  cutoutData: centerCutoutInfo.holeObj.serialize(),
                  offset: MathsUtils.serializeVector2(centerCutoutInfo.offset)
                }
              }),
              shapeGroupList: shape.shapeGroupList.map(shapeGroup => {
                return {
                  groupData: shapeGroup.serialize()
                }
              })
            }
          }
        })
      }
    }

    // Item entry
    this.editor.component.displayModal(LibraryItemDialog, {
      isCreateMode: true,
      typeFilter: [EditorTypes.LibraryItemTypes.assembly]
    }, libraryItem, { mountBody: true })
    .then(libraryItemInfo => {
      if(libraryItemInfo) {
        this.editor.component.successToast('Item Created')
        this.editor.component.updateLibrary()
      }
    })
  }


  /**
   * Remove selected segments & other objects from the shape
   */
  _removeSelected(selectedSegmentList, selectedStartSegmentList, selectedObjects) {
    var updatedShapeList = []

    this.editor.startUndoGroup('Remove items')

    selectedSegmentList.forEach(segmentInfo => {
      
      if(!updatedShapeList.find(listShape => listShape.id == segmentInfo.shape.id)) {
        updatedShapeList.push(segmentInfo.shape)

        this.editor.addUndoShape(segmentInfo.shape)
      }

      // Remove segment from the shape
      segmentInfo.shape.removeSegmentById(segmentInfo.segmentId)
    })

    // Vertices
    selectedStartSegmentList.forEach(segmentStartInfo => {
      // Ignore if already removed (from above)
      if(!segmentStartInfo.shape.getSegmentById(segmentStartInfo.segmentId))
        return

      if(!updatedShapeList.find(listShape => listShape.id == segmentStartInfo.shape.id)) {
        updatedShapeList.push(segmentStartInfo.shape)

        this.editor.addUndoShape(segmentStartInfo.shape)
      }

      segmentStartInfo.shape.removeSegmentById(segmentStartInfo.segmentId)      
    })

    // Other objects
    selectedObjects.forEach(selectedObj => {
      if(selectedObj.parentObject instanceof GlassShape) {
        if(!updatedShapeList.find(listShape => listShape.id == selectedObj.parentObject.id)) {
          updatedShapeList.push(selectedObj.parentObject)
          this.editor.addUndoShape(selectedObj.parentObject)
        }

        if(selectedObj instanceof GlassPanelItem)
          selectedObj.parentObject.removePanelItem(selectedObj)

        if(selectedObj instanceof GlassBaseHole)
          selectedObj.parentObject.removeCenterCutout(selectedObj)
        if(selectedObj instanceof GlassSplitLine)
          selectedObj.parentObject.removeSplitLine(selectedObj)
        if(selectedObj instanceof GlassDatumLine)
          selectedObj.parentObject.removeDatumLine(selectedObj)
        if(selectedObj instanceof GlassShapeGroup)
          selectedObj.parentObject.removeShapeGroup(selectedObj, { removeSegments: true })
        
      }
    })

    this.editor.closeCurrentUndoGroup()

    // Clear selection
    this.clearSelection()

    updatedShapeList.forEach(shape => {
      this.editor.onShapeUpdated(shape)
    })

    this.editor.render()
  }


  /**
   * Clone selected objects
   * Does not clone segments / panels
   */
  _cloneSelected(selectedObjects) {
    var updatedShapeList = []

    // Note: Only supporting center cutouts for the moment
    // Note: All objects must be from the same shape
    let cloneObjectList = selectedObjects.filter(obj => obj instanceof GlassBaseHole)
    if(cloneObjectList.length == 0)
      return

    // Calculate bounds for all objects
    let totalBounds = MathsUtils.calculateBoundsForBoxList(cloneObjectList.map(obj => obj.calculateSegmentBounds()))
    let boundsCenter = totalBounds.getCenter(new THREE.Vector2())

    // Calculate area on shape to place the new objects
    let cloneTarget = cloneObjectList[0].parentObject.findValidEmptyRegionInShape(boundsCenter, totalBounds)
    if(!cloneTarget)
      return

    let cloneOffset = cloneTarget.clone().sub(boundsCenter)

    this.editor.startUndoGroup('Cloning objects')

    selectedObjects.forEach(object => {

      // Center Cutout / Hole
      if(object instanceof GlassBaseHole) {
        if(!updatedShapeList.find(listShape => listShape.id == object.parentObject.id)) {
          updatedShapeList.push(object.parentObject)

          this.editor.addUndoShape(object.parentObject)
        }

        // Clone the object
        if(object.datumLinks.length > 0) {
          let newHole = object.clone()
          var holePos = object.calculateAnchorWorldPoint()
          holePos.add(cloneOffset)

          // Calculate datum links to a position using cloneOffset
          try {
            EditorDatumUtils.calculateClosestDatumLinks(newHole.parentObject, newHole.datumLinks, holePos, { ignoreAllHoles: true })
            newHole.rebuildHoleShape()
            object.parentObject.addCenterCutout(newHole)
          } catch(err) {
            console.error(err)
            newHole.dispose()
          }
        }
      }
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      shape.updateSceneObject()
      this.editor.onShapeUpdated(shape)
    })

    this.clearSelection()
  }


  /**
   * Update edgework details for all selected segments
   */
  _updateEdgework(selectedSegmentList, edgeworkConfigId, edgeworkSettingValue) {
    var updatedShapeList = []

    edgeworkConfigId = parseInt(edgeworkConfigId || 0)

    this.editor.startUndoGroup('Updating Edgework')

    selectedSegmentList.forEach(segmentInfo => {
      var segment = segmentInfo.shape.getSegmentForIndex(segmentInfo.segmentIndex)
      if(segment.isEdgeworkAvailable) {

        if(!updatedShapeList.find(listShape => listShape.id == segmentInfo.shape.id)) {
          updatedShapeList.push(segmentInfo.shape)

          this.editor.addUndoShape(segmentInfo.shape)
        }

        segmentInfo.shape.updateSegmentEdgework(segment, edgeworkConfigId, edgeworkSettingValue)
      }
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      shape.updateSceneObject()
    })
    this.editor.render()

    this.clearSelection()
  }


  /**
   * Start the tool to add points to a segment
   */
  _startAddSegmentPointsTool(selectedSegmentList) {
    // Should only be editing 1 segment
    if(selectedSegmentList.length != 1)
      return

    this.clearSelection()
    this.editor.render()

    let segmentInfo = selectedSegmentList[0]

    this.editor.startTool(GlassDesignerTypes.ToolTypes.segmentAddPoints, {
      glassShape: segmentInfo.shape,
      glassSegmentIndex: segmentInfo.segmentIndex
    })
  }


  _convertToArc(selectedSegmentList) {
    var updatedShapeList = []

    this.editor.startUndoGroup('Convert to arc')

    selectedSegmentList.forEach(segmentInfo => {
      var prevSegment = segmentInfo.shape.getSegmentForIndex(segmentInfo.segmentIndex-1)
      var segment = segmentInfo.shape.getSegmentForIndex(segmentInfo.segmentIndex)
      var nextSegment = segmentInfo.shape.getSegmentForIndex(segmentInfo.segmentIndex+1)

      if(!updatedShapeList.find(listShape => listShape.id == segmentInfo.shape.id)) {
        updatedShapeList.push(segmentInfo.shape)
        this.editor.addUndoShape(segmentInfo.shape)
      }

      // Reset corner radius of previous/next segments
      prevSegment.endCornerRadius = 0
      segment.startCornerRadius = 0
      segment.endCornerRadius = 0
      nextSegment.startCornerRadius = 0

      segment.convertToArc()
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      shape.updateSceneObject()
      this.editor.onShapeUpdated(shape)
    })
    this.editor.render()

    // Reupdate the tool popover buttons & selection
    this.displayToolSettingsPopover()
    this.displayEditSelectionControls()
  }

  _convertToLine(selectedSegmentList) {
    var updatedShapeList = []

    this.editor.startUndoGroup('Convert to line')

    selectedSegmentList.forEach(segmentInfo => {
      var prevSegment = segmentInfo.shape.getSegmentForIndex(segmentInfo.segmentIndex-1)
      var segment = segmentInfo.shape.getSegmentForIndex(segmentInfo.segmentIndex)
      var nextSegment = segmentInfo.shape.getSegmentForIndex(segmentInfo.segmentIndex+1)

      if(!updatedShapeList.find(listShape => listShape.id == segmentInfo.shape.id)) {
        updatedShapeList.push(segmentInfo.shape)
        this.editor.addUndoShape(segmentInfo.shape)
      }

      // Reset corner radius of previous/next segments
      prevSegment.endCornerRadius = 0
      segment.startCornerRadius = 0
      segment.endCornerRadius = 0
      nextSegment.startCornerRadius = 0

      segment.convertToLine()
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      shape.updateSceneObject()
      this.editor.onShapeUpdated(shape)
    })

    // Reupdate the tool popover buttons
    this.displayToolSettingsPopover()
    this.displayEditSelectionControls()
  }


  _convertVertexToArc(selectedStartSegmentList) {
    this.editor.startUndoGroup('Convert vertex to arc')

    let cornerSegInfo = selectedStartSegmentList[0]

    let shape = cornerSegInfo.shape
    var prevSegment = shape.getSegmentForIndex(cornerSegInfo.segmentIndex-1)
    var segment = shape.getSegmentForIndex(cornerSegInfo.segmentIndex)
    var nextSegment = shape.getSegmentForIndex(cornerSegInfo.segmentIndex+1)

    this.editor.addUndoShape(shape)
    
    // Create a new segment from the corner radius
    let roundCornerData = MathsUtils.calculateCornerRounding(
      prevSegment.startPosition,
      prevSegment.endPosition,
      segment.startPosition,
      segment.endPosition,
      segment.startCornerRadius,
      false
    )
    if(roundCornerData) {
      prevSegment.endPosition.copy(roundCornerData.segment1End)
      prevSegment.endCornerRadius = 0
      segment.startCornerRadius = 0
      segment.startPosition.copy(roundCornerData.segment2Start)

      // Create an arc
      let arcSegment = segment.clone()
      arcSegment.type = GlassDesignerTypes.SegmentTypes.arc
      arcSegment.arcRadius = roundCornerData.radius
      arcSegment.arcCenterPosition.copy(roundCornerData.center)
      arcSegment.startPosition.copy(roundCornerData.segment1End)
      arcSegment.endPosition.copy(roundCornerData.segment2Start)

      arcSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.startPosition)
      arcSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.endPosition)
      arcSegment.arcClockwise = !roundCornerData.clockwise
      arcSegment.isArcFromCornerRadius = true
      arcSegment.arcUpdated()

      shape.segmentList.splice(cornerSegInfo.segmentIndex, 0, arcSegment)
    }

    this.editor.closeCurrentUndoGroup()

    shape.segmentListUpdated()
    shape.updateSceneObject()

    this.editor.onShapeUpdated(shape)
    this.editor.render()

    this.clearSelection()
  }

  _convertArcToVertex(selectedSegmentList) {
    let segmentInfo = selectedSegmentList[0]

    let shape = segmentInfo.shape
    var prevSegment = shape.getSegmentForIndex(segmentInfo.segmentIndex-1)
    var segment = shape.getSegmentForIndex(segmentInfo.segmentIndex)
    var nextSegment = shape.getSegmentForIndex(segmentInfo.segmentIndex+1)

    // Prev/Next segments must be lines
    if(prevSegment.type != EditorTypes.SegmentTypes.line || nextSegment.type != EditorTypes.SegmentTypes.line) {
      this.editor.component.errorMessage('Cannot convert to corner', 'Neighbouring lines must be straight lines')
      return
    }

    // Make sure the previous & next segments intersect & are not paralell
    let intersection = MathsUtils.projectLineIntersection(prevSegment.startPosition, prevSegment.endPosition, nextSegment.startPosition, nextSegment.endPosition)
    if(!intersection) {
      this.editor.component.errorMessage('Cannot convert to corner', 'Neighbouring lines do not intersect to create a corner')
      return
    }

    this.editor.startUndoGroup('Convert arc to vertex')
    this.editor.addUndoShape(shape)

    // Remove arc & set corner radius on prev/next segments
    prevSegment.endPosition.set(intersection.x, intersection.y)
    nextSegment.startPosition.set(intersection.x, intersection.y)

    prevSegment.endCornerRadius = segment.arcRadius
    nextSegment.startCornerRadius = segment.arcRadius
    shape.segmentList.splice(segmentInfo.segmentIndex, 1)


    this.editor.closeCurrentUndoGroup()

    shape.segmentListUpdated()
    shape.updateSceneObject()

    this.editor.onShapeUpdated(shape)
    this.editor.render()

    this.clearSelection()
  }


  _updateArcEdit(selectedSegmentList, arcEditType, arcRadius, arcHeight, arcClockwise) {
    if(selectedSegmentList.length == 0)
      return

    let shape = selectedSegmentList[0].shape

    // Go into highlight shape mode
    this.editor.startShapeEditingMode(shape)

    selectedSegmentList.forEach(segmentInfo => {
      var segment = this.editor.highlightShape.getSegmentForIndex(segmentInfo.segmentIndex)

      if(segment.type == EditorTypes.SegmentTypes.arc) {

        // Clockwise must be set before updating arc by radius
        segment.arcClockwise = !!arcClockwise
        segment.arcEditType = arcEditType
        if(segment.arcEditType == EditorTypes.ArcEditTypes.radius)
          segment.updateArcByRadius(arcRadius)
        if(segment.arcEditType == EditorTypes.ArcEditTypes.arcHeight)
          segment.updateArcByHeight(arcHeight)
      }
    })

    this.editor.highlightShape.updateSceneObject()
    this.editor.render()
  }


  _cancelArcEdit(selectedSegmentList) {
    this.editor.endShapeEditingMode()

    this.clearSelection()
  }


  _applyArcEdit(selectedSegmentList, arcEditType, arcRadius, arcHeight, arcClockwise) {
    var updatedShapeList = []

    this.editor.endShapeEditingMode()
    this.editor.startUndoGroup('Update Arc')

    try {
      selectedSegmentList.forEach(segmentInfo => {
        var segment = segmentInfo.shape.getSegmentForIndex(segmentInfo.segmentIndex)

        if(segment.type == EditorTypes.SegmentTypes.arc) {

          if(!updatedShapeList.find(listShape => listShape.id == segmentInfo.shape.id)) {
            updatedShapeList.push(segmentInfo.shape)
            this.editor.addUndoShape(segmentInfo.shape)
          }

          // Clockwise must be set before updating arc by radius
          segment.arcClockwise = !!arcClockwise
          segment.arcEditType = arcEditType
          if(segment.arcEditType == EditorTypes.ArcEditTypes.radius)
            segment.updateArcByRadius(arcRadius)
          if(segment.arcEditType == EditorTypes.ArcEditTypes.arcHeight)
            segment.updateArcByHeight(arcHeight)
        }
      })
    } catch(err) {
      // Undo anything done by the failed skew shape
      this.editor.closeCurrentUndoGroup()
      this.editor.undoLastAction()

      this.editor.component.errorMessage('Error updating arc', err.message)
      return
    }

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      this.editor.onShapeUpdated(shape)
    })

    this.clearSelection()
  }


  _cancelDatumEdit(selectedDatumLineList) {
    this.clearSelection()
  }


  _applyDatumEdit(selectedDatumLineList, isDimensionDisplayed) {
    var updatedShapeList = []

    this.editor.startUndoGroup('Update Datum')

    selectedDatumLineList.forEach(datumLine => {
      if(datumLine.parentObject && datumLine.parentObject instanceof GlassShape) {
        if(!updatedShapeList.find(listShape => listShape.id == datumLine.parentObject.id)) {
          updatedShapeList.push(datumLine.parentObject)
          this.editor.addUndoShape(datumLine.parentObject)
        }
      }

      datumLine.isDimensionDisplayed = isDimensionDisplayed
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      this.editor.onShapeUpdated(shape)
    })
  }


  _cancelCutoutEdit(selectedSegmentList) {
    this.editor.clearSelection()
    this.editor.component.hidePopover()
  }


  _applyCutoutEdit(selectedSegmentList, isCutoutPolished) {
    var updatedShapeList = []

    this.editor.endShapeEditingMode()
    this.editor.startUndoGroup('Update Cutout polish')

    selectedSegmentList.forEach(segmentInfo => {
      var segment = segmentInfo.shape.getSegmentForIndex(segmentInfo.segmentIndex)

      if(!updatedShapeList.find(listShape => listShape.id == segmentInfo.shape.id)) {
        updatedShapeList.push(segmentInfo.shape)

        this.editor.addUndoShape(segmentInfo.shape)
      }

      segment.isCutoutPolished = isCutoutPolished
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      this.editor.onShapeUpdated(shape)
    })

    this.editor.endShapeEditingMode()
    this.clearSelection()
  }
  _applyCornerRadiusEdit(selectedStartSegmentList, cornerRadius) {
    var updatedShapeList = []

    this.editor.endShapeEditingMode()
    this.editor.startUndoGroup('Update Corner Radius')

    selectedStartSegmentList.forEach(segmentInfo => {
      if(!updatedShapeList.find(listShape => listShape.id == segmentInfo.shape.id)) {
        updatedShapeList.push(segmentInfo.shape)

        this.editor.addUndoShape(segmentInfo.shape)
      }

      segmentInfo.shape.updateSegmentStartCornerRadius(segmentInfo.segmentIndex, cornerRadius)
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      shape.updateSceneObject()
      this.editor.onShapeUpdated(shape)
    })

    this.editor.endShapeEditingMode()
    this.clearSelection()
  }


  _cancelCenterCutoutEdit() {
    this.editor.clearSelection()
    this.editor.component.hidePopover()
  }


  _applyCenterCutoutEdit(cutoutInfo) {
    var updatedShapeList = []

    var selectedCenterCutoutList = this.editor.getSelected().filter(object => object instanceof GlassBaseHole)

    this.editor.startUndoGroup('Update Center Cutout')

    selectedCenterCutoutList.forEach(centerCutout => {

      if(!updatedShapeList.find(listShape => listShape.id == centerCutout.parentObject.id)) {
        updatedShapeList.push(centerCutout.parentObject)

        this.editor.addUndoShape(centerCutout.parentObject)
      }

      centerCutout.updateCutoutSettings(cutoutInfo)
    })

    // Run recalculation on all center cutouts for every shape
    updatedShapeList.forEach(shape => {
      shape.internalItemsUpdated()
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      this.editor.onShapeUpdated(shape)
    })

    this.clearSelection()
  }

  _combineCenterCutouts() {
    var selectedCenterCutoutList = this.editor.getSelected().filter(object => object instanceof GlassBaseHole)

    try {
      let updatedShapeList = []
      this.editor.startUndoGroup('Update Center Cutout')

      if(selectedCenterCutoutList.length <= 1)
        throw new EditorUserError('Multiple center cutouts must be selected')

      selectedCenterCutoutList.forEach(centerCutout => {
        if(!updatedShapeList.find(listShape => listShape.id == centerCutout.parentObject.id)) {
          updatedShapeList.push(centerCutout.parentObject)
          this.editor.addUndoShape(centerCutout.parentObject)
        }
      })

      if(updatedShapeList.length != 1)
        throw new EditorUserError('Cannot combine cutouts from multiple shapes')
        
      // Run the center cutout combine function
      updatedShapeList[0].combineCenterCutouts(selectedCenterCutoutList)
      this.editor.onShapeUpdated(updatedShapeList[0])

      this.editor.closeCurrentUndoGroup()
    } catch(err) {
      // Cleanup any failed combine
      this.editor.closeCurrentUndoGroup()
      this.editor.undoLastAction()

      this.editor.component.errorMessage('Error combining cutouts', err.message)
    }

    this.clearSelection()
  }

  _cancelVertexEdit() {
    this.editor.clearSelection()
    this.editor.component.hidePopover()
  }
  _applyVertexEdit(selectedStartSegmentList, vertexInfo) {
    var updatedShapeList = []

    this.editor.startUndoGroup('Update Vertex')

    selectedStartSegmentList.forEach(segmentInfo => {
      if(!updatedShapeList.find(listShape => listShape.id == segmentInfo.shape.id)) {
        updatedShapeList.push(segmentInfo.shape)
        this.editor.addUndoShape(segmentInfo.shape)
      }

      segmentInfo.shape.updateSegmentStartCornerRadius(segmentInfo.segmentIndex, vertexInfo.cornerRadius)
      segmentInfo.shape.updateSegmentStartCornerPolished(segmentInfo.segmentIndex, vertexInfo.cornerPolished)
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      this.editor.onShapeUpdated(shape)
    })

    this.clearSelection()
  }

  _cancelGroupEdit() {
    this.editor.clearSelection()
    this.editor.component.hidePopover()
  }
  _applyGroupEdit(selectedShapeGroupList, groupInfo) {
    var updatedShapeList = []

    this.editor.startUndoGroup('Update Group')

    selectedShapeGroupList.forEach(shapeGroup => {
      if(!updatedShapeList.find(listShape => listShape.id == shapeGroup.parentObject.id)) {
        updatedShapeList.push(shapeGroup.parentObject)
        this.editor.addUndoShape(shapeGroup.parentObject)
      }

      shapeGroup.cutoutMeasurementType = groupInfo.cutoutMeasurementType
      shapeGroup.setGroupCutoutPolished(groupInfo.isGroupCutoutPolished)
      shapeGroup.isCutoutStockOverride = groupInfo.isCutoutStockOverride
      shapeGroup.updateStockOverrideList(groupInfo.cutoutStockOverrideList)
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      this.editor.onShapeUpdated(shape)
    })

    this.clearSelection()
  }


  _openShapeGroup(selectedShapeGroupList) {
    var updatedShapeList = []
    
    this.editor.startUndoGroup('Opening cutout group')

    selectedShapeGroupList.forEach(shapeGroup => {
      if(!updatedShapeList.find(listShape => listShape.id == shapeGroup.parentObject.id)) {
        updatedShapeList.push(shapeGroup.parentObject)
        this.editor.addUndoShape(shapeGroup.parentObject)
      }

      shapeGroup.parentObject.removeShapeGroup(shapeGroup, { removeSegments: false })
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      this.editor.onShapeUpdated(shape)
    })

    this.clearSelection()
  }

  _createShapeGroup() {
    var selectedShapeList = this._getSelectedShapeList()

    // Cannot group vertices across multiple shapes (or no shapes)
    if(selectedShapeList.length == 0)
      throw new EditorUserError('Nothing selected')
    if(selectedShapeList.length > 1)
      throw new EditorUserError('Cannot create a group from multiple panels')

    var shape = selectedShapeList[0]
    var selectedIndexList = this._calculateCutoutSelectedSegmentList(shape)

    let totalSelected = shape.segmentList.filter(segment => segment.isSegmentSelected).length

    var segmentList = selectedIndexList.map(index => shape.segmentList[index])
    if(segmentList.length < totalSelected)
      throw new EditorUserError('All selected edges must be connected')
    if(segmentList.length <= 1)
      throw new EditorUserError('At least two edges must be selected')


    this.editor.startUndoGroup('Create Group')
    this.editor.addUndoShape(shape)

    shape.updateCutoutGroup(segmentList[0].startPosition, segmentList[segmentList.length-1].endPosition, {})

    this.editor.closeCurrentUndoGroup()

    this.editor.onShapeUpdated(shape)
    this.clearSelection()
  }


  /**
   * DEVELOPER: Display log details for the selected segments
   */
  _developerLogSelection() {
    var selectedShapeList = this._getSelectedShapeList()
    let selectedObjList = this.editor.getSelected()

    console.log(`Selected Shapes:`)
    selectedShapeList.forEach((shape, shapeIndex) => {
      console.log(`   Shape #${shapeIndex}:`)
      console.log(`      ${JSON.stringify(shape.itemData, null, 8)}`)

      console.log(`      SelectedSegments:`)
      var segmentList = shape.segmentList.filter(segment => segment.isSegmentStartSelected)

      segmentList.forEach((segment, segmentIndex) => {
        console.log(`         Segment #${segmentIndex}:`)
        console.log(`            ${JSON.stringify(segment, null, 14)}`)
      })

      console.log(`      ShapeData:`)
      console.log(`         ${JSON.stringify(shape.generateShapeData(), null, 8)}`)
    })
    console.log(`Selected objects:`)
    selectedObjList.forEach((obj, objIndex) => {
      if(obj instanceof GlassShape)
        return

      console.log(`   Obj #${objIndex}:`)
      console.log(`      ${JSON.stringify(obj.serialize(), null, 8)}`)

      if(obj instanceof GlassShapeGroup) {
        obj.segmentIdList.forEach((segId, index) => {
          let segment = obj.parentObject.segmentList.find(s => s.id == segId)
          if(segment)
            console.log(`      Segment #${index} id: ${segment.id}, type: ${segment.type}, start: ${segment.startPosition.x},${segment.startPosition.y}, end: ${segment.endPosition.x},${segment.endPosition.y}`)
        })        
      }
    })
  }


  /**
   * Developer: Round the selected segments to nearest x decimal places
   * Helps with some shape issues after cutting
   */
  _developerRoundSelection(selectedSegmentList) {
    var updatedShapeList = []
    var devRoundPlaces = 5

    this.editor.startUndoGroup('Rounding Segments')

    selectedSegmentList.forEach(segmentInfo => {
      var segment = segmentInfo.shape.getSegmentForIndex(segmentInfo.segmentIndex)

      if(!updatedShapeList.find(listShape => listShape.id == segmentInfo.shape.id)) {
        updatedShapeList.push(segmentInfo.shape)
        this.editor.addUndoShape(segmentInfo.shape)
      }

      segment.startPosition = MathsUtils.round2DVector(segment.startPosition, devRoundPlaces)
      segment.endPosition = MathsUtils.round2DVector(segment.endPosition, devRoundPlaces)
      segment.arcCenterPosition = MathsUtils.round2DVector(segment.arcCenterPosition, devRoundPlaces)

      segment.arcUpdated()
    })

    this.editor.closeCurrentUndoGroup()

    updatedShapeList.forEach(shape => {
      shape.updateSceneObject()
      this.editor.onShapeUpdated(shape)
    })
    this.editor.render()

    this.clearSelection()
  }


  _squareOffSelection(selectedStartSegmentList) {
    this.editor.startUndoGroup('Square Off')
    let cornerSegInfo = selectedStartSegmentList[0]
    let shape = cornerSegInfo.shape    

    this.editor.addUndoShape(shape)

    try {
      // Rotate the selected segment start list so it starts correctly
      let segmentIndexList = selectedStartSegmentList.map(s => s.segmentIndex)
      segmentIndexList = Utils.wrapIndexListCyclic(segmentIndexList, shape.segmentList.length)

      var segmentList = []

      // Also include the previous segment of the first selected start segment
      // Used for projecting out the new corner point
      if(segmentIndexList.length > 0)
        segmentIndexList.unshift(segmentIndexList[0] - 1)

      segmentIndexList.forEach(segmentIndex => {
        // Ignore if already removed (from above)
        let seg = shape.getSegmentForIndex(segmentIndex)
        if(seg)
          segmentList.push(seg)
      })

      // Ignore arcs in the selection - only use the points
      if(segmentList.some(s => s.type == EditorTypes.SegmentTypes.arc)) {
        throw new EditorUserError('Cannot square off selection with arcs. Only straight line segments can be included in the selection')
      }

      var firstSeg = segmentList[0]
      var lastSeg = segmentList[segmentList.length-1]
      var origFirstSeg = firstSeg.clone()
      var origLastSeg = lastSeg.clone()

      // Project the end of the first segment and start of the last segment out to find the new corner point
      // Find intersection points
      // Update the segments & remove everything inside

      // Remove everything between the first & last segment (not including the segments themselves)
      for(let i=1; i<segmentList.length-1; i++) {
        shape.removeSegmentById(segmentList[i].id, { ignoreSegmentListUpdates: true })
      }

      // Project the segments out
      // If still near the group object, use the projection as a guess how to remove cutout
      // If too far out, ignore & user will have to fix it
      let projectedConnPoint = MathsUtils.projectLineIntersection(origFirstSeg.startPosition, origFirstSeg.endPosition, origLastSeg.startPosition, origLastSeg.endPosition)
      if(projectedConnPoint) {
        firstSeg.endPosition.copy(projectedConnPoint)
        lastSeg.startPosition.copy(projectedConnPoint)
      }

    } catch(err) {
      // Undo anything done by the failed square off
      this.editor.closeCurrentUndoGroup()
      this.editor.undoLastAction()

      this.editor.component.errorMessage('Error squaring off', err.message)
      return
    }

    this.editor.closeCurrentUndoGroup()

    shape.segmentListUpdated()
    shape.updateSceneObject()

    this.editor.onShapeUpdated(shape)
    this.editor.render()

    this.clearSelection()
  }


  _fitArcsSelection(selectedStartSegmentList) {
    this.editor.startUndoGroup('Fit Arcs')

    let cornerSegInfo = selectedStartSegmentList[0]
    let shape = cornerSegInfo.shape
    
    this.editor.addUndoShape(shape)

    try {
      var pointList = []

      // Rotate the selected segment start list so it starts correctly
      let segmentIndexList = selectedStartSegmentList.map(s => s.segmentIndex)
      segmentIndexList = Utils.wrapIndexListCyclic(segmentIndexList, shape.segmentList.length)

      var segmentList = []

      segmentIndexList.forEach((segmentIndex, listIndex) => {
        // Ignore if already removed (from above)
        let seg = shape.getSegmentForIndex(segmentIndex)
        if(seg) {
          // Don't include the last segment (only the point)
          if(listIndex < segmentIndexList.length - 1)
            segmentList.push(seg)

          pointList.push(seg.startPosition.clone())
        }
      })

      // Ignore arcs in the selection - only use the points
      if(segmentList.some(s => s.type == EditorTypes.SegmentTypes.arc)) {
        throw new EditorUserError('Cannot fit arcs selection with existing arcs - only straight line segments can be included in the selection')
      }

      var fittedArc = MathsUtils.findBestFitArcForPointList(pointList)
      if(!fittedArc)
        throw new EditorUserError('Could not fit arc to selection')

      var insertIndex = shape.segmentList.findIndex(listSeg => listSeg.id == segmentList[0].id)

      // Insert new arc segment
      var arcSegment = new GlassSegment(GlassDesignerTypes.SegmentTypes.arc);
      arcSegment.startPosition.copy(pointList[0])
      arcSegment.endPosition.copy(pointList[pointList.length - 1])
      arcSegment.arcRadius = fittedArc.arcRadius
      arcSegment.arcCenterPosition.copy(fittedArc.arcCenterPosition)
      arcSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.startPosition)
      arcSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(arcSegment.arcCenterPosition, arcSegment.endPosition)
      arcSegment.arcUpdated()
      shape.segmentList.splice(insertIndex + 1, 0, arcSegment)

      // Remove selected
      segmentList.forEach(segment => {
        let segmentIndex = shape.segmentList.findIndex(listSeg => listSeg.id == segment.id)
        if(segmentIndex != -1) {
          shape.segmentList.splice(segmentIndex, 1)
        }
      })

      // Reconnect arc into the shape
      let arcSegmentIndex = shape.segmentList.findIndex(seg => seg.id == arcSegment.id)
      if(arcSegmentIndex != -1) {
        shape.getSegmentForIndex(arcSegmentIndex-1).endPosition.copy(arcSegment.startPosition)
        shape.getSegmentForIndex(arcSegmentIndex-1).positionsUpdated()
        shape.getSegmentForIndex(arcSegmentIndex+1).startPosition.copy(arcSegment.endPosition)
        shape.getSegmentForIndex(arcSegmentIndex+1).positionsUpdated()
      }
    } catch(err) {
      // Undo anything done by the failed fit arcs
      this.editor.closeCurrentUndoGroup()
      this.editor.undoLastAction()

      this.editor.component.errorMessage('Error fitting arc', err.message)
      return
    }

    this.editor.closeCurrentUndoGroup()

    shape.segmentListUpdated()
    shape.updateSceneObject()

    this.editor.onShapeUpdated(shape)
    this.editor.render()

    this.clearSelection()
  }


  /**
   * Display the tool settings popover for this tool
   */
  displayToolSettingsPopover() {
    var toolInfoText = ''

    // Get selected segments
    this.selectedSegmentList = []
    this.selectedStartSegmentList = []
    this.selectedList = this.editor.getSelected()

    var selectedShapeList = this._getSelectedShapeList()
    var selectedCenterCutoutList = this.editor.getSelected().filter(object => object instanceof GlassBaseHole)
    var selectedDatumLineList = this.editor.getSelected().filter(object => object instanceof GlassDatumLine)
    var selectedShapeGroupList = this.editor.getSelected().filter(object => object instanceof GlassShapeGroup)

    var isEditable = selectedShapeList.filter(shape => shape.isItemEditable()).length > 0
    var isVertexAvailable = false
    var isGroupAvailable = false
    var isEdgeworkAvailable = false
    var panelCornerRadiusValue = 0
    var isToolsAvailable = false
    var isCutoutEditAvailable = false
    var isCutoutCenterEditAvailable = false
    var isCloneAvailable = false
    var isCutoutAvailable = false
    var isCutoutCenterAvailable = false
    var isCutoutCombineAvailable = false
    var isAddPointsAvailable = false
    var isAddLibraryShapeAvailable = false
    var isAddLibraryKitAvailable = false
    var isConvertToArcAvailable = false
    var isConvertToLineAvailable = false
    var isConvertVertexToArcAvailable = false
    var isConvertArcToVertexAvailable = false
    var isArcEditAvailable = false
    var isDatumEditAvailable = false
    var isGroupCreateAvailable = false
    var isGroupOpenAvailable = false
    var arcRadius = 0
    var arcHeight = 0
    var arcClockwise = false
    var arcEditType = EditorTypes.ArcEditTypes.radius
    var isCutoutPolished = false
    var vertexCornerRadiusValue = 0
    var vertexCornerPolished = false
    var centerCutoutSettings = {}
    var selectedEdgeworkConfigIdList = []
    var selectedEdgeworkSettingList = []
    var groupMeasureType = GlassDesignerTypes.CutoutMeasurementTypes.center
    var isGroupCutoutPolished = false

    this.optionRemoveAvailable = true
    

    // Calculate details available based on selection
    selectedShapeList.forEach(selectedShape => {
      selectedShape.segmentList.forEach((segment, segmentIndex) => {
        if(segment.getSegmentSelected()) {
          this.selectedSegmentList.push({
            shape: selectedShape,
            segmentIndex: segmentIndex,
            segmentId: segment.id,
            startCornerRadius: segment.startCornerRadius,
            endCornerRadius: segment.endCornerRadius,
            isEdgeworkAvailable: segment.isEdgeworkAvailable,
            isCutoutPolished: segment.isCutoutPolished
          })

          if(segment.isEdgeworkAvailable && segment.orderEditorEdgeworkConfigId != 0) {
            selectedEdgeworkConfigIdList.push(segment.orderEditorEdgeworkConfigId)
            selectedEdgeworkSettingList.push(segment.edgeworkSettingValue)
          }
        }
        if(segment.getSegmentStartSelected()) {
          this.selectedStartSegmentList.push({
            shape: selectedShape,
            segmentIndex: segmentIndex,
            segmentId: segment.id,
            startCornerRadius: segment.startCornerRadius
          })

          vertexCornerRadiusValue = segment.startCornerRadius
          vertexCornerPolished = segment.isStartCornerPolished
        }
      })
    })

    // Calculate edgework info
    if(this.selectedSegmentList.filter(segment => segment.isEdgeworkAvailable).length > 0) {
      isEdgeworkAvailable = true
    }
    if(this.selectedSegmentList.filter(segment => !segment.isEdgeworkAvailable).length > 0) {
      isCutoutEditAvailable = true

      // Get polished flag
      if(this.selectedSegmentList.filter(segment => segment.isCutoutPolished).length > 0)
        isCutoutPolished = true
    }

    if(this.selectedStartSegmentList.length > 0) {
      isVertexAvailable = true
      if(this.selectedStartSegmentList.length == 1 && this.selectedStartSegmentList[0].startCornerRadius >= 1.0)
        isConvertVertexToArcAvailable = true

      // Need at least 2 vertices for tools to be available
      isToolsAvailable = this.selectedStartSegmentList.length > 1
    }


    // Calculate cutout availability
    // If 1 shape & contiguous list of segments selected & not closed, can create a cutout or group the segments
    if(selectedShapeList.length == 1) {
      var shape = selectedShapeList[0]
      var selectedIndexList = this._calculateCutoutSelectedSegmentList(shape)

      let totalSelected = shape.segmentList.filter(segment => segment.getSegmentSelected()).length
      var cutoutSegmentList = selectedIndexList.map(index => shape.segmentList[index])
      if(cutoutSegmentList.length == totalSelected && cutoutSegmentList.length >= 1) {      

        // Make sure it is not closed
        var pointList = MathsUtils.convertSegmentListToPointList(cutoutSegmentList)
        if(!MathsUtils.is2DPolygonClosed(pointList)) {
          isCutoutAvailable = true
          isGroupCreateAvailable = true
        }
      }

      // If the entire shape is selected, allow 'add shape' tool
      if(cutoutSegmentList.length == shape.segmentList.length) {
        isAddLibraryShapeAvailable = true
      }
    }

    // If multiple shapes selected, and all segments selected, allow 'add kit' tool
    if(selectedShapeList.length > 1) {
      let totalKitSegments = selectedShapeList.reduce((count, shape) => count + shape.segmentList.length, 0)
      let totalSelectedKitSegments = selectedShapeList.reduce((count, shape) => count + shape.segmentList.filter(seg => seg.getSegmentSelected()).length, 0)

      if(totalKitSegments == totalSelectedKitSegments)
        isAddLibraryKitAvailable = true
    }

    // Single Segment selected
    // Check conversion tools
    // Check arc editing
    if(this.selectedSegmentList.length == 1) {
      var segment = this.selectedSegmentList[0].shape.getSegmentForIndex(this.selectedSegmentList[0].segmentIndex)

      if(segment.type == EditorTypes.SegmentTypes.line) {
        isConvertToArcAvailable = true
        isAddPointsAvailable = true
      } else if(segment.type == EditorTypes.SegmentTypes.arc) {
        isConvertToLineAvailable = true
        
        isArcEditAvailable = true
        arcRadius = MathsUtils.roundNumber(segment.arcRadius, 1)
        arcHeight = segment.calculateArcHeight()
        arcEditType = segment.arcEditType || EditorTypes.ArcEditTypes.radius
        arcClockwise = segment.arcClockwise

        // If a small arc, allow convert to corner
        // TODO: Needs to be a 90 degree arc test
        if(arcRadius <= 300) {
          isConvertArcToVertexAvailable = true
        }
      }
    }

    // Check if datum lines selected
    if(selectedDatumLineList.length > 0) {
      isDatumEditAvailable = true
    }

    // Check if shape groups selected
    if(selectedShapeGroupList.length > 0) {
      isGroupOpenAvailable = true
      isGroupAvailable = true

      if(selectedShapeGroupList.length == 1)
        isCutoutAvailable = true

      groupMeasureType = selectedShapeGroupList[0].cutoutMeasurementType
      isGroupCutoutPolished = selectedShapeGroupList[0].isGroupCutoutPolished()
    }

    // If any center cutouts selected, allow center cutout editing
    // Ignore if segments selected too
    if(selectedCenterCutoutList.length > 0) {
      isVertexAvailable = false
      isConvertVertexToArcAvailable = false
      isGroupAvailable = false
      isToolsAvailable = false

      if(this.selectedSegmentList.length == 0) {
        isCutoutCenterEditAvailable = true
        isCutoutCenterAvailable = true

        // Clone/Combine only available if cutouts all from same shape
        if(selectedShapeList.length == 1) {
          isCloneAvailable = true

          // Combine available if multiple cutouts selected
          if(selectedCenterCutoutList.length > 1)
            isCutoutCombineAvailable = true
        }
        
        centerCutoutSettings = {
          holeType: selectedCenterCutoutList[0].holeType,
          angle: selectedCenterCutoutList[0].angle,
          radius: selectedCenterCutoutList[0].radius,
          width: selectedCenterCutoutList[0].width,
          height: selectedCenterCutoutList[0].height,
          cornerRadius: selectedCenterCutoutList[0].cornerRadiusTL,
          isCutoutPolished: selectedCenterCutoutList[0].isCutoutPolished,
          isCountersunk: selectedCenterCutoutList[0].isCountersunk,
          countersunkRadius: selectedCenterCutoutList[0].countersunkRadius,
          measureToHorizontal: selectedCenterCutoutList[0].measureToHorizontal,
          measureToVertical: selectedCenterCutoutList[0].measureToVertical
        }

        // Custom cutout info
        if(centerCutoutSettings.holeType == GlassDesignerTypes.LibraryItemShapeTypes.custom) {
          let bounds = selectedCenterCutoutList[0].calculateSegmentBounds()
          centerCutoutSettings.width = Math.round(bounds.max.x - bounds.min.x)
          centerCutoutSettings.height = Math.round(bounds.max.y - bounds.min.y)
        }
      }
    }


    
    // If segments/vertices across multiple shapes selected, disable certain tools
    if(selectedShapeList.length > 1) {
      this.optionRemoveAvailable = false
      isCutoutAvailable = false
      isCutoutCombineAvailable = false
      isGroupCreateAvailable = false
      isConvertToArcAvailable = false
      isConvertToLineAvailable = false
      isConvertArcToVertexAvailable = false
      isAddPointsAvailable = false
      isGroupOpenAvailable = false
      isGroupAvailable = false
      isToolsAvailable = false
    }

    // If the shape cannot have its segment list shape editable, disable certain tools
    if(selectedShapeList.length > 0) {
      let hasUneditableShapes = selectedShapeList.filter(shape => !shape.isSegmentListShapeEditable()).length > 0
      if(hasUneditableShapes && (this.selectedSegmentList.length > 0 || this.selectedStartSegmentList.length > 0)) {
        this.optionRemoveAvailable = false
        isCutoutAvailable = false
        isCutoutCombineAvailable = false
        isArcEditAvailable = false
        isConvertToArcAvailable = false
        isConvertToLineAvailable = false
        isConvertArcToVertexAvailable = false
        isAddPointsAvailable = false
        isGroupOpenAvailable = false
        isGroupAvailable = false
        isVertexAvailable = false
        isToolsAvailable = false
        isConvertVertexToArcAvailable = false
      }
    }

    // Hide all tools if not editable
    if(!isEditable) {
      isToolsAvailable = false
    }

    var displayOptions = {
      horizontalPosition: this._calculatePopoverHorizontalPosition()
    }

    this.editor.component.displaySelectionToolPopover({
      isEditable: isEditable,
      isVertexAvailable: isVertexAvailable,
      isGroupAvailable: isGroupAvailable,
      isToolsAvailable: isToolsAvailable,
      isEdgeworkAvailable: isEdgeworkAvailable,
      isCutoutEditAvailable: isCutoutEditAvailable,
      isCutoutCenterEditAvailable: isCutoutCenterEditAvailable,
      isCutoutCombineAvailable: isCutoutCombineAvailable,
      centerCutoutSettings: centerCutoutSettings,
      selectedEdgeworkConfigIdList: _.uniq(selectedEdgeworkConfigIdList),
      selectedEdgeworkSettingList: selectedEdgeworkSettingList,
      panelCornerRadiusValue: panelCornerRadiusValue,
      isRemoveAvailable: this.optionRemoveAvailable,
      isCloneAvailable: isCloneAvailable,
      isCutoutAvailable: isCutoutAvailable,
      isCutoutCenterAvailable: isCutoutCenterAvailable,
      isAddPointsAvailable: isAddPointsAvailable,
      isConvertToArcAvailable: isConvertToArcAvailable,
      isConvertToLineAvailable: isConvertToLineAvailable,
      isConvertVertexToArcAvailable: isConvertVertexToArcAvailable,
      isConvertArcToVertexAvailable: isConvertArcToVertexAvailable,
      isArcEditAvailable: isArcEditAvailable,
      isDatumEditAvailable: isDatumEditAvailable,
      selectedDatumLineList: selectedDatumLineList,
      isAddLibraryShapeAvailable: isAddLibraryShapeAvailable,
      isAddLibraryKitAvailable: isAddLibraryKitAvailable,
      isGroupCreateAvailable: isGroupCreateAvailable,
      isGroupOpenAvailable: isGroupOpenAvailable,
      arcRadius: arcRadius,
      arcHeight: arcHeight,
      arcClockwise: arcClockwise,
      arcEditType: arcEditType,
      isCutoutPolished: isCutoutPolished,
      vertexCornerRadiusValue: vertexCornerRadiusValue,
      vertexCornerPolished: vertexCornerPolished,
      groupMeasureType: groupMeasureType,
      isGroupCutoutPolished: isGroupCutoutPolished,
      glassShapeGroup: selectedShapeGroupList[0],
      infoText: toolInfoText,
      context: this,
      cancelCallback: (ctx, userCancelled) => {
        if(userCancelled)
          ctx.editor.cancelActiveTool()
      },
      updateCallback: (ctx, updateType, info) => {
        switch(updateType) {
          case 'updateArcEdit':
            ctx._updateArcEdit(ctx.selectedSegmentList, info.arcEditType, info.arcRadius, info.arcHeight, info.arcClockwise)
            break
        }
      },
      actionCallback: (ctx, updateType, info) => {
        switch(updateType) {
          case 'remove':
            ctx._removeSelected(ctx.selectedSegmentList, ctx.selectedStartSegmentList, ctx.selectedList)
            break
          case 'clone':
            ctx._cloneSelected(ctx.selectedList)
            break
          case 'removeEdgework':
            ctx._updateEdgework(ctx.selectedSegmentList, null, 0)
            break
          case 'applyEdgework':
            ctx._updateEdgework(ctx.selectedSegmentList, info.edgeworkConfigId, info.edgeworkSettingValue)
            break
          case 'addPoints':
            ctx._startAddSegmentPointsTool(ctx.selectedSegmentList)
            break
          case 'convertToArc':
            ctx._convertToArc(ctx.selectedSegmentList)
            break
          case 'convertToLine':
            ctx._convertToLine(ctx.selectedSegmentList)
            break
          case 'convertVertexToArc':
            ctx._convertVertexToArc(ctx.selectedStartSegmentList)
            break
          case 'convertArcToVertex':
            ctx._convertArcToVertex(ctx.selectedSegmentList)
            break
          case 'cancelArcEdit':
            ctx._cancelArcEdit(ctx.selectedSegmentList)
            break
          case 'applyArcEdit':
            ctx._applyArcEdit(ctx.selectedSegmentList, info.arcEditType, info.arcRadius, info.arcHeight, info.arcClockwise)
            break
          case 'cancelDatumEdit':
            ctx._cancelDatumEdit(selectedDatumLineList)
            break
          case 'applyDatumEdit':
            ctx._applyDatumEdit(selectedDatumLineList, info.isDimensionDisplayed)
            break
          case 'cancelCutoutEdit':
            ctx._cancelCutoutEdit(ctx.selectedSegmentList)
            break
          case 'applyCutoutEdit':
            ctx._applyCutoutEdit(ctx.selectedSegmentList, info.isCutoutPolished)
            break
          case 'cancelCenterCutoutEdit':
            ctx._cancelCenterCutoutEdit()
            break
          case 'applyCenterCutoutEdit':
            ctx._applyCenterCutoutEdit(info)
            break
          case 'combineCutout':
            ctx._combineCenterCutouts()
            break
          case 'cancelVertexEdit':
            ctx._cancelVertexEdit()
            break
          case 'applyVertexEdit':
            ctx._applyVertexEdit(ctx.selectedStartSegmentList, info)
            break
          case 'cancelGroupEdit':
            ctx._cancelGroupEdit()
            break
          case 'applyGroupEdit':
            ctx._applyGroupEdit(selectedShapeGroupList, info)
            break
          case 'openGroup':
            ctx._openShapeGroup(selectedShapeGroupList)
            break
          case 'createGroup':
            ctx._createShapeGroup()
            break
          case 'addShapeCutout':
            ctx._createLibraryShapeCutoutItem()
            break
          case 'addCenterCutout':
            ctx._createLibraryCenterCutoutItem()
            break
          case 'addShape':
            ctx._createLibraryShapeKitItem()
            break
          case 'addKit':
            ctx._createLibraryShapeKitItem()
            break

          // Tools
          case 'squareOffSelection':
            ctx._squareOffSelection(ctx.selectedStartSegmentList)
            break
          case 'fitArcsSelection':
            ctx._fitArcsSelection(ctx.selectedStartSegmentList)
            break

          // Developer tools
          case 'developerLogSelection':
            ctx._developerLogSelection()
            break
          case 'developerRoundSelection':
            ctx._developerRoundSelection(ctx.selectedSegmentList)
            break
        }
      },
      applyCallback: (ctx, info) => {
      }
    }, displayOptions)
  }


  /**
   * Display the shape popover
   */
  displayShapePopover(selectedShape) {
    var displayOptions = {
      horizontalPosition: this._calculatePopoverHorizontalPosition()
    }

    // Don't allow add/remove for NCR panels or assemblies
    this.optionRemoveAvailable = true
    let isCloneAvailable = true

    if(this.editor.getOrder().isNCROrder() || selectedShape.isPartOfAssemblyGroup()) {
      this.optionRemoveAvailable = false
      isCloneAvailable = false
    }


    // Display the shape properties popover
    this.editor.component.displayShapePopover({
      shape: selectedShape,
      itemData: selectedShape.itemData,
      isRemoveAvailable: this.optionRemoveAvailable,
      isCloneAvailable: isCloneAvailable,
      context: this,
      updateCallback: (ctx, options) => {        
      },
      cancelCallback: (ctx, userCancelled) => {
      },
      applyCallback: (ctx, info) => {
        
      },
      actionCallback: (ctx, action) => {
        switch(action) {
          case 'remove':
            ctx.shapeActionRemoveSelectedShape(selectedShape)
            break
          case 'resetPanel':
            ctx.shapeActionResetSelectedShape(selectedShape)
            break
          case 'rotatePanel':
            ctx.shapeActionRotateSelectedShape(selectedShape)
            break
          case 'mirrorXPanel':
            ctx.shapeActionMirrorXSelectedShape(selectedShape)
            break
          case 'mirrorYPanel':
            ctx.shapeActionMirrorYSelectedShape(selectedShape)
            break
          case 'expandPanel':
            ctx.shapeActionExpandSelectedShape(selectedShape)
            break
          case 'shrinkPanel':
            ctx.shapeActionShrinkSelectedShape(selectedShape)
            break
          case 'cleanupPanel':
            ctx.shapeActionCleanupSelectedShape(selectedShape)
            break
          case 'snapPointsPanel':
            ctx.shapeActionSnapPointsSelectedShape(selectedShape)
            break
          case 'correctRotationPanel':
            ctx.shapeActionCorrectRotationSelectedShape(selectedShape)
            break
          case 'clone':
            ctx.shapeActionCloneSelectedShape(selectedShape)
            break
          case 'createLibraryShape':
            ctx._createLibraryShapeKitItem()
            break
          case 'update':
            ctx.shapeActionDisplayUpdateShapeDialog(selectedShape)
            break
        }
      }
    }, displayOptions)
  }

  /**
   * Display the assembly popover
   */
  displayAssemblyPopover(selectedAssembly) {
    var displayOptions = {
      horizontalPosition: this._calculatePopoverHorizontalPosition()
    }

    // Don't allow add/remove for NCR panels
    this.optionRemoveAvailable = true
    let isCloneAvailable = true

    if(this.editor.getOrder().isNCROrder()) {
      this.optionRemoveAvailable = false
      isCloneAvailable = false
    }

    // Display the assembly properties popover
    this.editor.component.displayAssemblyPopover({
      assembly: selectedAssembly,
      itemData: selectedAssembly.itemData,
      isRemoveAvailable: this.optionRemoveAvailable,
      isCloneAvailable: isCloneAvailable,
      context: this,
      updateCallback: (ctx, options) => {        
      },
      cancelCallback: (ctx, userCancelled) => {
      },
      applyCallback: (ctx, info) => {
        
      },
      actionCallback: (ctx, action) => {
        switch(action) {
          case 'remove':
            ctx.shapeActionRemoveSelectedAssembly(selectedAssembly)
            break
          case 'createLibraryShape':
            ctx._createLibraryAssemblyItem()
            break
          case 'update':
            ctx.shapeActionDisplayUpdateAssemblyDialog(selectedAssembly)
            break
        }
      }
    }, displayOptions)
  }


  /**
   * Display the datum popover
   */
  displayDatumPopover(selectedDatum) {

    // Display the datum properties popover
    this.editor.component.displayDatumPopover({
      datumData: {
        length: selectedDatum.getLength()
      },
      context: this,
      updateCallback: (ctx, options) => {        
      },
      cancelCallback: (ctx, userCancelled) => {
      },
      applyCallback: (ctx, info) => {
        
      },
      actionCallback: (ctx, action) => {
        switch(action) {
          case 'remove':
            ctx.datumRemoveDatumLine(selectedDatum)
            break
        }
      }
    })
  }


  /**
   * Remove the selected datum line from the editor
   * Closes this tool afterwards
   */
  datumRemoveDatumLine(selectedDatum) {
    this.editor.removeDatumLine(selectedDatum)

    this.clearSelection()
  }


  /**
   * Remove the selected shape from the editor
   * Closes this tool afterwards
   */
  shapeActionRemoveSelectedShape(selectedShape) {
    this.editor.removeGlassShape(selectedShape)

    this.clearSelection()
  }


  /**
   * Remove the selected assembly from the editor
   * Closes this tool afterwards
   */
  shapeActionRemoveSelectedAssembly(selectedAssembly) {
    this.editor.removeAssembly(selectedAssembly)

    this.clearSelection()
  }


  /**
   * Reset the selected shape back to a standard shape with no editing or child objects
   * Closes this tool afterwards
   */
  shapeActionResetSelectedShape(selectedShape) {
    this.editor.resetGlassShape(selectedShape)

    this.clearSelection()
  }


  /**
   * Rotate the selected shape by 90 degrees
   */
  shapeActionRotateSelectedShape(selectedShape) {
    this.editor.rotateGlassShape(selectedShape)
  }


  /**
   * Mirror the selected shape by on X axis
   */
  shapeActionMirrorXSelectedShape(selectedShape) {
    this.editor.mirrorGlassShape(selectedShape, true, false)
  }


  /**
   * Mirror the selected shape by on Y axis
   */
  shapeActionMirrorYSelectedShape(selectedShape) {
    this.editor.mirrorGlassShape(selectedShape, false, true)
  }


  /**
   * Expand the selected shape
   */
  shapeActionExpandSelectedShape(selectedShape) {
    this.editor.startTool(GlassDesignerTypes.ToolTypes.shapeExpandShrinkTool, {
      glassShape: selectedShape,
      isExpandMode: true
    })
  }


  /**
   * Shrink the selected shape
   */
  shapeActionShrinkSelectedShape(selectedShape) {
    this.editor.startTool(GlassDesignerTypes.ToolTypes.shapeExpandShrinkTool, {
      glassShape: selectedShape,
      isShrinkMode: true
    })
  }


  /**
   * Cleanup the selected shape segments
   */
  shapeActionCleanupSelectedShape(selectedShape) {
    try {
      this.editor.cleanupGlassShapeSegments(selectedShape)
    } catch(err) {
      if(err instanceof EditorUserError || err instanceof UserError)
        this.editor.component.errorMessage('Error cleaning up shape', err.message)
      else
        throw err
    }
  }


  /**
   * Snap the selected shape points
   */
  shapeActionSnapPointsSelectedShape(selectedShape) {
    try {
      this.editor.snapGlassShapeSegments(selectedShape)
    } catch(err) {
      if(err instanceof EditorUserError || err instanceof UserError)
        this.editor.component.errorMessage('Error snapping shape points', err.message)
      else
        throw err
    }
  }


  /**
   * Correct the rotation on the selected shape
   */
  shapeActionCorrectRotationSelectedShape(selectedShape) {
    try {
      this.editor.correctRotationShape(selectedShape)
    } catch(err) {
      if(err instanceof EditorUserError || err instanceof UserError)
        this.editor.component.errorMessage('Error correcting rotation', err.message)
      else
        throw err
    }
  }


  /**
   * Clone the selected shape in the editor
   */
  shapeActionCloneSelectedShape(selectedShape) {

    // Clone the shape
    // Move it across to the next free space on the right of all the shapes
    var availablePosition = this.editor.getAvailableShapePosition(selectedShape.calculateSegmentBounds())

    this.editor.startUndoGroup('Clone Glass Item')

    var newShape = selectedShape.clone()

    // Set the position of it
    let newShapeBounds = newShape.calculateSegmentBounds()
    newShape.moveShape(new THREE.Vector2(availablePosition.x - newShapeBounds.min.x, availablePosition.y - newShapeBounds.min.y))

    newShape.segmentListUpdated()
    newShape.updateSceneObject()

    this.editor.onShapeUpdated(newShape)
    this.editor.shapeListUpdated()

    this.editor.addUndoShape(newShape, EditorTypes.UndoCommandTypes.add)
    this.editor.closeCurrentUndoGroup()

    this.clearSelection()

    // View all shapes
    this.editor.moveCameraToAllGlassShapes()
  }


  /**
   * Display the edit/update panel dialog for the selected shape
   */
  shapeActionDisplayUpdateShapeDialog(selectedShape) {

    this.editor.component.displayModal(GlassItemDialog, {
      isCreateMode: false,
      contactId: this.editor.getOrder().contactId,
      defaultSalesRateId: this.editor.getOrder().newCustomerDefaultSalesRateId,
      editorConfigData: this.editor.getEditorConfigurationData()
    }, {
      stockId: selectedShape.itemData.stockId,
      stockPriceGroupId: selectedShape.itemData.stockPriceGroupId,
      description: selectedShape.itemData.description,
      mark: selectedShape.itemData.mark,
      notes: selectedShape.itemData.notes,
      quantity: selectedShape.itemData.quantity,
      unitPrice: selectedShape.itemData.unitPrice,
      unitPriceOverridden: selectedShape.itemData.unitPriceOverridden,
      glassLevelId: selectedShape.itemData.glassLevelId,
      glassLevel: selectedShape.itemData.glassLevel,
      grainDirection: selectedShape.itemData.grainDirection,
      stockFaceSideUpType: selectedShape.itemData.stockFaceSideUpType,
      isStamped: selectedShape.isStamped
    }, { mountBody: true })
    .then((itemData) => {
      if(itemData) {
        
        this.editor.startUndoGroup('Editing Glass Item')
        this.editor.addUndoShape(selectedShape)

        // OrderItem based settings
        // If stock/price group updated, lock the unit price so it gets reset
        if(selectedShape.itemData.stockId != itemData.stockId || selectedShape.itemData.stockPriceGroupId != itemData.stockPriceGroupId)
          itemData.unitPriceOverridden = false

        selectedShape.itemData.stockId = itemData.stockId
        selectedShape.itemData.stockPriceGroupId = itemData.stockPriceGroupId
        selectedShape.itemData.description = itemData.description
        selectedShape.itemData.mark = itemData.mark
        selectedShape.itemData.notes = itemData.notes
        selectedShape.itemData.quantity = itemData.quantity
        selectedShape.itemData.unitPriceOverridden = itemData.unitPriceOverridden
        selectedShape.itemData.unitPrice = itemData.unitPrice
          
        selectedShape.itemData.glassLevelId = itemData.glassLevelId

        selectedShape.itemData.grainDirection = itemData.grainDirection
        selectedShape.itemData.stockFaceSideUpType = itemData.stockFaceSideUpType
        
        selectedShape.itemData.stock = _.clone(itemData.stock || {})
        selectedShape.itemData.stockPriceGroup = _.clone(itemData.stockPriceGroup || {})
        selectedShape.itemData.glassLevel = _.clone(itemData.glassLevel || {})
        
        // For stock linked shapes, change the size
        if(selectedShape.isStockLinked() && selectedShape.itemData.glassLevelId) {
          selectedShape.resizeShape(itemData.glassLevel.width, itemData.glassLevel.height)
        }

        // Update default edgework
        if(selectedShape.defaultEdgeworkConfigId != itemData.defaultEdgeworkConfigId) {
          selectedShape.defaultEdgeworkConfigId = parseInt(itemData.defaultEdgeworkConfigId || 0)
          selectedShape.applyDefaultEdgework()
        }

        // Update stamp item
        selectedShape.isStamped = itemData.isStamped
        selectedShape.updateDefaultStampItem()

        // Rebuild shape
        selectedShape.updateSceneObject()
        this.editor.onShapeUpdated(selectedShape)

        this.editor.closeCurrentUndoGroup()
      }      
    })

    this.clearSelection()
  }


  /**
   * Display the edit/update assembly dialog for the selected assembly
   */
  shapeActionDisplayUpdateAssemblyDialog(selectedAssembly) {

    this.editor.component.displayModal(EditorAssemblyItemDialog, {
      isCreateMode: false,
      contactId: this.editor.getOrder().contactId,
      defaultSalesRateId: this.editor.getOrder().newCustomerDefaultSalesRateId,
      editorConfigData: this.editor.getEditorConfigurationData()
    }, {
      stockId: selectedAssembly.itemData.stockId,
      stockPriceGroupId: selectedAssembly.itemData.stockPriceGroupId,
      description: selectedAssembly.itemData.description,
      mark: selectedAssembly.itemData.mark,
      notes: selectedAssembly.itemData.notes,
      quantity: selectedAssembly.itemData.quantity,
      unitPrice: selectedAssembly.itemData.unitPrice,
      unitPriceOverridden: selectedAssembly.itemData.unitPriceOverridden,
      assemblyOrderItemList: selectedAssembly.itemData.assemblyOrderItemList
    }, { mountBody: true })
    .then((itemData) => {
      if(itemData) {
        
        // Update assembly details (mark, pricing, etc)
        this.editor.startUndoGroup('Editing Glass Item')
        this.editor.addUndoShape(selectedAssembly)

        selectedAssembly.itemData.description = itemData.description
        selectedAssembly.itemData.mark = itemData.mark
        selectedAssembly.itemData.notes = itemData.notes
        selectedAssembly.itemData.quantity = itemData.quantity
        selectedAssembly.itemData.unitPriceOverridden = itemData.unitPriceOverridden
        selectedAssembly.itemData.unitPrice = itemData.unitPrice

        // Update certain items (general / assemblies) from the dialog
        // Glass shapes need to be edited directly via shape itself
        // 
        itemData.assemblyOrderItemList.forEach(updatedItem => {
          // Only update general stock / assemblies
          if(updatedItem.stockType != InventoryStockTypes.general && updatedItem.stockType != InventoryStockTypes.assembly)
            return

          // Find link by 'assemblyLinkUUID'
          let assemblyOrderItem = selectedAssembly.itemData.assemblyOrderItemList.find(assItem => assItem.assemblyLinkUUID == updatedItem.assemblyLinkUUID)
          if(assemblyOrderItem) {
            assemblyOrderItem.stockId = updatedItem.stockId
            assemblyOrderItem.description = updatedItem.description
            assemblyOrderItem.mark = updatedItem.mark
          }
        })

        // Rebuild shape
        selectedAssembly.updateSceneObject()
        this.editor.onAssemblyUpdated(selectedAssembly)

        this.editor.closeCurrentUndoGroup()
      }      
    })

    this.clearSelection()
  } 


  /**
   * Receive an object event from objects we're listening too
   */
  onReceiveObjectEvent(object, eventType, data) {
    if(eventType == GlassDesignerTypes.ObjectEventTypes.objectCaptured) {
      if(object == this.editor.getMoveHandleObject()) {
        let selectionList = this.editor.getSelected()
        let shapeObjects = selectionList.filter(object => object instanceof GlassShape) 
        let holeObjects = selectionList.filter(object => object instanceof GlassBaseHole)
        let panelItemObjects = selectionList.filter(object => object instanceof GlassPanelItem)

        if(!this.isEditingAvailable())
          return

        // Hole / Center Cutout move tool
        if(holeObjects.length > 0) {
          this.editor.startTool(GlassDesignerTypes.ToolTypes.itemMoveTool, {
            direction: data.direction
          })
          return
        }
        // Item move tool
        if(panelItemObjects.length > 0) {
          this.editor.startTool(GlassDesignerTypes.ToolTypes.itemMoveTool, {
            direction: data.direction
          })
          return
        }

        // Entire shape move
        if(this.selectedSegmentList.length == 0 && this.selectedStartSegmentList.length == 0 && shapeObjects.length == 1) {
          if(!this.isEditingAvailable())
            return
            
          // Shape move
          this.editor.startTool(GlassDesignerTypes.ToolTypes.itemMoveTool, {
            direction: data.direction
          })
          return
        }

        // Start segment move tool
        if(this.selectedSegmentList.length == 1 || this.selectedStartSegmentList.length <= 2) {
          if(!this.isEditingAvailable())
            return
            
          // Segment move
          this.editor.startTool(GlassDesignerTypes.ToolTypes.segmentMoveTool, {
            direction: data.direction
          })

          return
        }
      }
    }

  }


  // Display the edit selection controls
  displayEditSelectionControls() {
    if(!this.isEditingAvailable())
      return

    let axisOnlyMove = true

    // If selected shapes are not editable, don't show selection controls
    let shapeList = this._getSelectedShapeList()
    let editableShapeList = shapeList.filter(shape => shape.isItemEditable())
    if(editableShapeList.length == 0)
      return

    let moveHandle = this.editor.getMoveHandleObject()
    if(!moveHandle)
      return
    moveHandle.setVisible(false)

    let selectionList = this.editor.getSelected()
    if(selectionList.length == 0)
      return

    var selectionBounds = this.editor.calculateSelectedObject2DBounds()

    // Support for selecting entire shapes
    var entireShapeSelected = false
    if(selectionList.length == 1 && (selectionList[0] instanceof GlassShape)) {
      if(this.selectedSegmentList.length == 0 && this.selectedStartSegmentList.length == 0)
        entireShapeSelected = true
    }

    // Only 1 segment or 2 segment starts can be selected to move
    // Currently cannot move a group of segments around until the solver is updated
    if(this.selectedSegmentList.length > 1 || this.selectedStartSegmentList.length > 2)
      return

    // If other non-shape objects selected, don't show move tool
    let nonMoveableObjects = selectionList.filter(object => !(object instanceof GlassShape) && !(object instanceof GlassBaseHole) && !(object instanceof GlassPanelItem))
    let holeObjects = selectionList.filter(object => object instanceof GlassBaseHole)
    let panelObjects = selectionList.filter(object => object instanceof GlassPanelItem)
    if(nonMoveableObjects.length > 0)
      return

    // If segments mixed with hole/panel objects, ignore
    if((holeObjects.length > 0 || panelObjects.length > 0) && (this.selectedSegmentList.length > 0 || this.selectedStartSegmentList.length > 0))
      return

    // If the shape's segment list is not editable, don't display move controls for segment/segment start points
    if(!entireShapeSelected && !editableShapeList[0].isSegmentListShapeEditable() && (this.selectedSegmentList.length > 0 || this.selectedStartSegmentList.length > 0))
      return

    var position = new THREE.Vector2(selectionBounds.min.x, selectionBounds.max.y)
    if(this.selectedSegmentList.length == 1) {
      var segment = this.selectedSegmentList[0].shape.getSegmentForIndex(this.selectedSegmentList[0].segmentIndex)
      position = segment.getMidpoint()
    }
    if(holeObjects.length > 0 || panelObjects.length > 0 || entireShapeSelected) {
      position = selectionBounds.getCenter(new THREE.Vector2())

      if(holeObjects.length > 0 || panelObjects.length > 0)
        position = selectionBounds.max

      // Free form move allowed
      // Note: Only for panels currently until other move tools updated
      if(panelObjects.length > 0 || holeObjects.length > 0 || entireShapeSelected)
        axisOnlyMove = false
    }

    moveHandle.setPosition(position)
    moveHandle.setVisible(true)
    moveHandle.setupHandles({
      horizontalEnabled: axisOnlyMove,
      verticalEnabled: axisOnlyMove,
      moveAllEnabled: !axisOnlyMove
    })
  }

  // Hide the edit selection controls
  hideEditSelectionControls() {
    this.editor.getMoveHandleObject().setVisible(false)
  }


  /**
   * Selection was updated
   */
  selectionUpdated() {
    // For all shapes associated with the selected objects, rerun the dimension helper to flag dimensions as highlighted for selected objects
    let shapeList = _.compact(this.editor.selectionList.map(object => {
      if(object instanceof GlassShape)
        return object
      if(object.parentObject && object.parentObject instanceof GlassShape)
        return object.parentObject
      return null      
    }))
    shapeList = _.uniqBy(shapeList, obj => obj.id)

    shapeList.forEach(shape => {
      this.editor.getDimensionHelper().updateGlassShapeDimensions(shape)
    })

    this.editor.flagRenderRequired()
  }

}
